import React, { useState, useContext, useCallback, useEffect, useRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { createPortal } from 'react-dom';
import { getPortalRoot } from '../utils/portalRoot';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from './Header';
import { formatPrice, formatProductName, getTranslatedValue, getSpecLabel } from './cart';
import { CartContext } from '../App';
import { getTranslation, Language } from '../translations';
import type { CartItem } from '../App';
import { FaStore, FaCheck, FaChevronLeft, FaChevronRight, FaShippingFast, FaMapMarkerAlt, FaLightbulb, FaChevronDown } from 'react-icons/fa';
import { MdLocalShipping } from 'react-icons/md';
import { FiMapPin, FiCalendar, FiClock, FiHardDrive, FiAward, FiBatteryCharging, FiHash, FiUser, FiMail, FiPhone, FiEdit2, FiTag, FiWifi, FiTool, FiMonitor, FiBox, FiCpu, FiServer, FiPackage } from 'react-icons/fi';
import { getSpecIcon as getSpecIconFromMapper } from '../utils/iconMapper';
import { TermsContent, PrivacyContent } from './LegalContent';
import {
  confirmReservation,
  fetchBookedSlotsForDate,
  type Appointment,
  type AppointmentSummary,
  type ServiceType,
  type CreateAppointmentParams
} from '../services/reservationService';

// Limits
const NOTES_MAX = 1000;
const NEWLINE_PENALTY = 4; // each line break costs extra 4 (total 5 incl. char)
const EMPTY_LINE_PENALTY = 50; // empty line costs extra 50

// Normalize newlines and limit consecutive empties
const sanitizeNotes = (text: string): string => {
  let normalized = text.replace(/\r\n?/g, '\n');
  // collapse 3+ consecutive newlines into two (max 1 empty line)
  normalized = normalized.replace(/\n{3,}/g, '\n\n');
  return normalized;
};

// Calculate effective length with penalties
const getEffectiveNotesLength = (text: string): number => {
  const s = sanitizeNotes(text);
  let effective = 0;
  let currentLineHasContent = false;
  let lastCharWasNewline = false;
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (ch === '\n') {
      // base cost of one char already accounted below by adding 1
      effective += 1 + NEWLINE_PENALTY;
      if (!currentLineHasContent || lastCharWasNewline) {
        effective += EMPTY_LINE_PENALTY;
      }
      currentLineHasContent = false;
      lastCharWasNewline = true;
    } else {
      effective += 1;
      currentLineHasContent = true;
      lastCharWasNewline = false;
    }
  }
  return effective;
};

// Truncate text so that effective length <= limit
const truncateNotesByLimit = (text: string, limit: number): string => {
  const s = sanitizeNotes(text);
  let effective = 0;
  let currentLineHasContent = false;
  let lastCharWasNewline = false;
  let endIndex = 0;
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    let delta = 0;
    if (ch === '\n') {
      delta = 1 + NEWLINE_PENALTY + (!currentLineHasContent || lastCharWasNewline ? EMPTY_LINE_PENALTY : 0);
      currentLineHasContent = false;
      lastCharWasNewline = true;
    } else {
      delta = 1;
      currentLineHasContent = true;
      lastCharWasNewline = false;
    }
    if (effective + delta > limit) break;
    effective += delta;
    endIndex = i + 1;
  }
  return s.slice(0, endIndex);
};

// Global styles for modal animation
const GlobalModalStyles = createGlobalStyle`
  @keyframes modalAppear {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  body.modal-open {
    overflow: hidden;
  }
`;

// Customer interface
interface Customer {
  id: string;
  personalInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  addressInfo: {
    street: string;
    postalCode: string;
    city: string;
    country: string;
  };
}

// Component props
interface CustomerServicePageProps {
  cartItems: CartItem[];
  total: number;
  onBack: () => void;
  onComplete: (customer: Customer, serviceOption: string, appointmentDetails?: any) => void;
  isSubmitting?: boolean;
  submitError?: string | null;
}

// Styled components
const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0;
  background: transparent;
  position: relative;
  z-index: 10;
`;

const HeaderBox = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  position: relative;
  
  .dark & {
    background: #0f1116; /* header neutral */
    border-color: #2e3441;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.55);
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    border-radius: 14px;
    margin-bottom: 1.25rem;
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 1rem 1.125rem;
    border-radius: 12px;
    margin-bottom: 1rem;
    gap: 0.5rem;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #64748b;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #0f172a;
    transform: translateX(-2px);
  }

  .dark & {
    background: #0f1216;
    border-color: #334155;
    color: #94a3b8;

    &:hover {
      background: #1e293b;
      border-color: #475569;
      color: #e2e8f0;
    }
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const BackArrowIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

const HeaderContent = styled.div`
  flex: 1;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0;
  line-height: 1.1;
  

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }

  @media (max-width: 480px) {
    font-size: 1.35rem;
  }

  .dark & { color: #e8eaef; }
`;

const PageSubtitle = styled.p`
  font-size: 1rem;
  color: #6e6e73;
  margin: 0.25rem 0 0;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
  @media (max-width: 480px) {
    font-size: 0.8125rem;
  }

  .dark & { color: #a9b1bc; }
`;

const MainLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 440px;
  gap: 2rem;
  /* stretch (i.p.v. start) zodat de rechterkolom de volledige rijhoogte van de
     lange linkerkolom krijgt — anders heeft het sticky-formulier geen ruimte om
     mee te scrollen met het proces. */
  align-items: stretch;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    display: flex;
    flex-direction: column;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 1024px) {
    gap: 1.5rem;
  }
`;

const ContentCard = styled.div`
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 10px 28px rgba(16, 24, 40, 0.06);
  border: 1px solid #e6edf5;
  padding: 2rem;
  position: relative;
  overflow: hidden;

  .dark & {
    background: #0f1216;
    border: 1px solid #2e3441;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.6);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 20px;
  }

  @media (max-width: 480px) {
    padding: 1.1rem 1rem;
    border-radius: 16px;
  }

  @media (min-width: 1025px) {
    order: 2;
  }

  @media (max-width: 1024px) {
    order: 2;
  }
`;

const SectionHeader = styled.h2`
  font-size: 0.7rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 1rem 0;

  .dark & { color: #64748b; }

  @media (max-width: 480px) {
    font-size: 0.65rem;
    margin-bottom: 0.75rem;
  }
`;

const FormGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem 1.25rem;
    align-items: end;
    background: transparent;
    padding: 0;
    margin-bottom: 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    align-items: stretch;
    gap: 1rem;
  }
  @media (max-width: 480px) {
    gap: 0.9rem;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  @media (max-width: 480px) { gap: 0.3rem; }
`;

const FormLabel = styled.label`
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    display: block;
    margin-bottom: 0;
    @media (max-width: 480px) { font-size: 0.84rem; }
    @media (max-width: 360px) { font-size: 0.82rem; }

    span {
      color: #ef4444;
      margin-left: 0.15rem;
    }

    .dark & { color: #d1d5db; }
`;

const InputWrapper = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    transition: color 0.2s ease;

  .dark & { color: #64748b; }
  @media (max-width: 480px) { left: 0.8rem; }
`;

const FormInput = styled.input`
     padding: 0.8rem 0.9rem;
    border: 1px solid #e5e7eb;
     border-radius: 10px;
     font-size: 0.95rem;
    background: #ffffff;
    color: #1e293b;
    transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
    width: 100%;

    &[data-with-icon="true"] {
      padding-left: 2.75rem;
    }

    @media (max-width: 480px) {
      padding: 0.75rem 0.85rem;
      font-size: 0.93rem;
      &[data-with-icon="true"] { padding-left: 2.5rem; }
    }
    @media (max-width: 360px) {
      padding: 0.7rem 0.8rem;
      font-size: 0.9rem;
    }

    &:hover {
      border-color: #d1d5db;
    }

    &:focus {
      outline: none;
      border-color: #374151;
      box-shadow: 0 0 0 3px rgba(55, 65, 81, 0.1);
    }

    &::placeholder {
    color: #c0c5ce;
    font-weight: 400;
  }

  .dark & {
    background: #111318;
    border-color: #2a2f3a;
    color: #e5e7eb;
  }
  .dark &::placeholder { color: #4b5563; }
  .dark &:hover { border-color: #374151; }
  .dark &:focus {
    border-color: #374151;
    box-shadow: 0 0 0 3px rgba(55, 65, 81, 0.15);
  }
`;

const FormTextArea = styled.textarea`
     padding: 0.8rem 0.9rem;
    border: 1px solid #e5e7eb;
     border-radius: 10px;
     font-size: 0.95rem;
    background: #ffffff;
    color: #1e293b;
    transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
    min-height: 100px;
    resize: vertical;
    width: 100%;
    font-family: inherit;

    &[data-with-icon="true"] {
      padding-left: 2.75rem;
    }

    @media (max-width: 480px) {
      padding: 0.75rem 0.85rem;
      font-size: 0.93rem;
    }

    &:hover {
      border-color: #d1d5db;
    }

    &:focus {
      outline: none;
      border-color: #374151;
      box-shadow: 0 0 0 3px rgba(55, 65, 81, 0.1);
    }

    &::placeholder {
    color: #c0c5ce;
    font-weight: 400;
  }
  
  .dark & {
    background: #111318;
    border-color: #2a2f3a;
    color: #e5e7eb;
  }
  .dark &::placeholder { color: #4b5563; }
  .dark &:hover { border-color: #374151; }
  .dark &:focus {
    border-color: #374151;
    box-shadow: 0 0 0 3px rgba(55, 65, 81, 0.15);
  }
`;

const FormSelectWrapper = styled.div`
  position: relative;
`;

const FormSelect = styled.select`
  padding: 0.875rem 2.5rem 0.875rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  background: #ffffff;
  color: #1d1d1f;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  appearance: none;
  width: 100%;
  @media (max-width: 480px) {
    padding: 0.8rem 2.2rem 0.8rem 0.9rem;
    border-radius: 10px;
    font-size: 0.98rem;
  }
  
  &:focus {
    outline: none;
    border-color: #6b7280;
    box-shadow: 0 0 0 3px rgba(107,114,128,0.15);
  }
  
  .dark & {
    background: #0f1116;
    border-color: #334155;
    color: #e5e7eb;
  }
`;

const FormSelectIcon = styled.div`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #9ca3af;
  
  .dark & { color: #94a3b8; }
  @media (max-width: 480px) { right: 0.8rem; }
`;

const CustomerTypeToggle = styled.div`
  display: flex;
  background: #f1f5f9;
  border-radius: 14px;
  padding: 4px;
  margin-bottom: 1.75rem;

  .dark & { background: #1a1f2e; }
`;

const ToggleButton = styled.button<{ selected: boolean }>`
  flex: 1;
  padding: 0.75rem 1.25rem;
  border-radius: 11px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.25s cubic-bezier(0.4, 0, 0.2, 1), color 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  background: ${({ selected }) => selected ? '#ffffff' : 'transparent'};
  color: ${({ selected }) => selected ? '#111827' : '#6b7280'};
  box-shadow: ${({ selected }) => selected ? '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)' : 'none'};
  letter-spacing: -0.01em;

  &:hover {
    color: ${({ selected }) => selected ? '#111827' : '#374151'};
  }

  .dark & {
    background: ${({ selected }) => selected ? '#2a2f3a' : 'transparent'};
    color: ${({ selected }) => selected ? '#f3f4f6' : '#6b7280'};
    box-shadow: ${({ selected }) => selected ? '0 1px 3px rgba(0,0,0,0.3)' : 'none'};
  }
`;

const ServiceSection = styled.div`
  margin-top: 0;
  padding-top: 0;
  border-top: none;
`;

const ServiceGrid = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`;

const ServiceOption = styled.div<{ selected: boolean }>`
  background: transparent;
  border: none;
  border-bottom: 1px dashed #e5e7eb;
  border-radius: 0;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;

  &:last-child {
    border-bottom: none;
  }

  .dark & {
    border-bottom-color: #1e293b;
  }
`;

const ServiceHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  padding: 0.875rem 0.25rem;
  justify-content: flex-start;
  flex-wrap: nowrap;
  transition: background 0.15s ease;
  border-radius: 8px;

  &:hover { background: #f8fafc; }

  .dark &:hover { background: #1e293b; }

  @media (max-width: 480px) {
    gap: 0.6rem;
    padding: 0.75rem 0.25rem;
  }
`;

const ServiceIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;

  .dark & {
    background: #1e293b;
    color: #94a3b8;
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    font-size: 1rem;
  }
`;

const ServiceContentWrapper = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.375rem;
  }
`;

const ServiceContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 640px) {
    width: 100%;
    flex: none;
  }

  @media (max-width: 480px) {
    flex: 1 1 0%;
    min-width: 0;
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 700;
  color: #111315;
  margin: 0 0 0.05rem 0;
  letter-spacing: -0.01em;
  line-height: 1.05;

  @media (min-width: 1025px) { font-size: 1.15rem; line-height: 1.1; }
  
  .dark & { color: #f1f5f9; }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.1;
    margin-bottom: 0.02rem;
  }
`;

const ServiceSubtitle = styled.p`
  font-size: 0.85rem;
  color: #64748b;
  margin: 0;
  line-height: 1.15;
  @media (min-width: 1025px) { font-size: 0.95rem; }
  .dark & { color: #cbd5e1; }
  @media (max-width: 480px) { font-size: 0.78rem; line-height: 1.1; }
`;

const ServicePrice = styled.div<{ type: 'free' | 'paid' | 'bonus' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: ${props => props.type === 'bonus' ? '#065f46' : props.type === 'free' ? '#065f46' : '#334155'};
  padding: 0.4rem 0.8rem;
  background: ${props => props.type === 'bonus'
    ? 'linear-gradient(135deg, #e8fff3 0%, #f6fff9 100%)'
    : props.type === 'free'
    ? 'linear-gradient(135deg, #eafff3 0%, #f5fff9 100%)'
    : 'linear-gradient(135deg, #f5f7fb 0%, #f8fafc 100%)'};
  border: 1px solid ${props => props.type === 'bonus' ? '#bbf7d0' : props.type === 'free' ? '#bbf7d0' : '#e2e8f0'};
  border-radius: 9999px;
  text-align: center;
  line-height: 1.05;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.9), 0 6px 16px rgba(0,0,0,0.06);
  position: relative;
  white-space: nowrap;
  flex-shrink: 0;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 9999px;
    background: ${props => (props.type === 'paid') ? '#94a3b8' : '#10b981'};
    box-shadow: 0 0 0 4px ${props => (props.type === 'paid') ? 'rgba(148,163,184,0.18)' : 'rgba(16,185,129,0.15)'};
    flex-shrink: 0;
  }
  
  .desktop-text {
    display: inline;
  }
  
  .mobile-text {
    display: none;
  }
  
  @media (min-width: 1025px) { font-size: 0.92rem; padding: 0.45rem 0.9rem; }
  
  .dark & {
    color: ${props => (props.type === 'free' || props.type === 'bonus') ? '#34d399' : '#e5e7eb'};
    background: ${props => (props.type === 'free' || props.type === 'bonus') ? 'rgba(52,211,153,0.16)' : 'rgba(107,114,128,0.2)'};
    border-color: ${props => (props.type === 'free' || props.type === 'bonus') ? 'rgba(52,211,153,0.3)' : 'rgba(107,114,128,0.35)'};
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
    padding: 0.4375rem 0.8125rem;
    font-weight: 700;
    gap: 0.4rem;
    line-height: 1.2;
    max-width: 140px;
    white-space: normal;
    text-align: center;
    letter-spacing: 0;
    min-height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    .desktop-text {
      display: none;
    }

    .mobile-text {
      display: inline;
    }

    &::before {
      width: 6px;
      height: 6px;
      flex-shrink: 0;
      box-shadow: 0 0 0 2.5px ${props => (props.type === 'paid') ? 'rgba(148,163,184,0.18)' : 'rgba(16,185,129,0.15)'};
    }
  }

  @media (max-width: 360px) {
    padding: 0.375rem 0.6875rem;
    font-size: 0.6875rem;
    gap: 0.35rem;
    min-height: 30px;
    max-width: 120px;

    &::before {
      width: 5px;
      height: 5px;
      flex-shrink: 0;
      box-shadow: 0 0 0 2px ${props => (props.type === 'paid') ? 'rgba(148,163,184,0.18)' : 'rgba(16,185,129,0.15)'};
    }
  }
`;

// Shake animation for coming-soon label
const shakeKeyframes = `
  @keyframes subtle-shake {
    10% { transform: translateX(-2px) rotate(-1deg); }
    20% { transform: translateX(2px) rotate(1deg); }
    30% { transform: translateX(-2px) rotate(-1deg); }
    40% { transform: translateX(2px) rotate(1deg); }
    50% { transform: translateX(-1px) rotate(-0.5deg); }
    60% { transform: translateX(1px) rotate(0.5deg); }
    70% { transform: translateX(0); }
  }
`;

// Inject keyframes once
const StyleInjector = styled.div`
  ${shakeKeyframes}
`;

const ServiceIndicator = styled.div<{ selected: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid ${props => props.selected ? '#6b7280' : '#d1d5db'};
  background: ${props => props.selected ? 'rgba(75,85,99,0.1)' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.8rem;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  position: relative;
  flex-shrink: 0;

  @media (min-width: 1025px) { width: 24px; height: 24px; }

  svg { display: none; }

  &:after {
    content: '';
    width: 10px;
    height: 10px;
    background: #4b5563;
    border-radius: 50%;
    opacity: ${props => props.selected ? 1 : 0};
    transition: opacity 0.2s ease;
  }
  
  .dark & {
    border-color: ${props => props.selected ? '#6b7280' : '#334155'};
    background: ${props => props.selected ? 'rgba(75,85,99,0.2)' : 'transparent'};
  }
  
  @media (max-width: 480px) {
    width: 18px; height: 18px;
  }
`;

// Expanded content components
const ExpandedContent = styled.div`
  padding: 1.5rem 1.5rem 1.25rem 1.5rem;
  border-top: 1px solid rgba(107, 114, 128, 0.25);
  margin-top: 1rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;

  .dark & {
    border-top-color: rgba(107,114,128,0.25);
  }

  @media (max-width: 480px) {
    padding: 0.875rem 0.75rem 0.75rem;
    margin-top: 0.625rem;
  }
`;

const StepContainer = styled.div`
  padding-top: 1.5rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  &:first-child {
    padding-top: 0;
  }

  @media (max-width: 480px) {
    padding-top: 1rem;
  }
`;

const SectionTitle = styled.div<{ $active?: boolean }>`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.$active ? '#374151' : '#1d1d1f'};
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: color 0.3s ease;
  flex: 1;
  min-width: 0;

  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 1rem;
    gap: 0.6rem;
  }
  
  @media (max-width: 360px) {
    font-size: 0.85rem;
    margin-bottom: 0.9rem;
    gap: 0.5rem;
  }
  
  .dark & {
    color: ${props => props.$active ? '#e5e7eb' : '#f1f5f9'};
  }
`;

const SectionDot = styled.div<{ $active?: boolean }>`
  width: 9px;
  height: 9px;
  background: ${props => props.$active ? '#6b7280' : '#d1d5db'};
  border-radius: 50%;
  position: relative;
  transition: background-color 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${props => props.$active ? 'scale(1.1)' : 'scale(1)'};

  .dark & {
    background: ${props => props.$active ? '#6b7280' : '#334155'};
  }
`;

const SectionHeaderWithNav = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  @media (max-width: 480px) {
    margin-bottom: 0.75rem;
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 0.35rem;
`;

const NavButton = styled.button<{ $active?: boolean; disabled?: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #4b5563;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  font-size: 0.85rem;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);

  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #6b7280;
    color: #374151;
  }
  
  &:disabled {
    opacity: 0.6;
  }
  
  &.active {
    background: #374151;
    border-color: #6b7280;
    color: white;
  }
  
  .dark & {
    border-color: #2e3441;
    background: #0e1116;
    color: #cbd5e1;
    box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.35);
  }
  .dark &:hover:not(:disabled) {
    background: #161a21;
    border-color: #3a4250;
    color: #e5e7eb;
  }
  .dark &.active {
    background: #2a2f3a;
    border-color: #3a4250;
    color: white;
  }
`;

// Date selection components
const DaysContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 0.2rem 0.1rem;
  margin: 0 -0.1rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  /* Hide scrollbar but keep functionality */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Enable smooth scrolling for trackpad */
  scroll-behavior: smooth;
`;

const DayCard = styled.button<{ selected?: boolean; disabled?: boolean; $isToday?: boolean }>`
  flex-shrink: 0;
  box-sizing: border-box;
  width: 74px;
  min-height: 86px;
  padding: 0.45rem 0.4rem 0.35rem;
  border-radius: 12px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  opacity: ${({ disabled }) => disabled ? 0.5 : 1};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.25s ease-in-out, border-color 0.25s ease-in-out, color 0.25s ease-in-out, box-shadow 0.25s ease-in-out;
  border: 1px solid ${({ selected, $isToday }) => {
    if (selected) return '#6b7280';
    if ($isToday) return '#d1d5db';
    return '#e5e7eb';
  }};
  background: ${({ selected, disabled }) => {
    if (selected) return '#374151';
    if (disabled) return '#f8f9fa';
    return '#ffffff';
  }};
  color: ${({ selected, disabled }) => {
    if (selected) return '#ffffff';
    if (disabled) return '#a1a1aa';
    return '#1d1d1f';
  }};

  @media (max-width: 480px) {
    width: 58px;
    min-height: 72px;
    padding: 0.4rem 0.35rem 0.3rem;
    border-radius: 10px;

    /* Ensure selected date is clearly visible on mobile */
    background: ${({ selected, disabled }) => {
      if (selected) return '#1f2937';
      if (disabled) return '#f8f9fa';
      return '#ffffff';
    }} !important;
    
    color: ${({ selected, disabled }) => {
      if (selected) return '#ffffff';
      if (disabled) return '#a1a1aa';
      return '#1d1d1f';
    }} !important;
    
    border: ${({ selected, $isToday }) => {
      if (selected) return '2px solid #374151';
      if ($isToday) return '1px solid #d1d5db';
      return '1px solid #e5e7eb';
    }} !important;
    
    box-shadow: ${({ selected }) => selected ? '0 4px 12px rgba(55, 65, 81, 0.3)' : 'none'};
    
    &:hover:not(:disabled) {
      background: ${({ selected }) => selected ? '#1f2937' : '#f3f4f6'} !important;
    }
  }
  
  @media (max-width: 360px) {
    width: 54px;
    min-height: 68px;
    padding: 0.35rem 0.3rem 0.28rem;
    border-radius: 10px;
  }
  
  &:hover:not(:disabled) { 
    background: #f3f4f6;
  }
  
  .dark & {
    border: 1px solid ${({ selected, $isToday }) => {
      if (selected) return '#3a4250';
      if ($isToday) return '#3a4250';
      return '#2e3441';
    }};
    background: ${({ selected, disabled }) => {
      if (selected) return '#2a2f3a';
      if (disabled) return '#12151c';
      return '#0e1116';
    }};
    color: ${({ selected, disabled }) => (selected ? '#ffffff' : (disabled ? '#8b94a4' : '#e5e7eb'))};
    
    @media (max-width: 480px) {
      background: ${({ selected, disabled }) => {
        if (selected) return '#1a1f27';
        if (disabled) return '#12151c';
        return '#0e1116';
      }} !important;
      
      color: ${({ selected, disabled }) => {
        if (selected) return '#ffffff';
        if (disabled) return '#8b94a4';
        return '#e5e7eb';
      }} !important;
      
      border: ${({ selected, $isToday }) => {
        if (selected) return '2px solid #3a4250';
        if ($isToday) return '1px solid #3a4250';
        return '1px solid #2e3441';
      }} !important;
      
      box-shadow: ${({ selected }) => selected ? '0 4px 12px rgba(0, 0, 0, 0.4)' : 'none'};
      
      &:hover:not(:disabled) {
        background: ${({ selected }) => selected ? '#1a1f27' : '#1a1f27'} !important;
      }
    }
  }
  
  .dark &:hover:not(:disabled) {
    background: #1a1f27;
  }
`;

const DayName = styled.div`
  font-size: 0.7rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.4px;

  @media (max-width: 480px) {
    font-size: 0.68rem;
    margin-bottom: 0.25rem;
  }

  @media (max-width: 360px) {
    font-size: 0.64rem;
    margin-bottom: 0.2rem;
  }
`;

const DayNumber = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 0.08rem;
  
  @media (max-width: 480px) {
    font-size: 1.05rem;
    margin-bottom: 0.08rem;
  }
  
  @media (max-width: 360px) {
    font-size: 0.98rem;
    margin-bottom: 0.06rem;
  }
`;

const DayMonth = styled.div`
  font-size: 0.68rem;
  font-weight: 600;
  color: #64748b;
  line-height: 1;
  @media (max-width: 480px) { font-size: 0.64rem; }
`;

const DayYear = styled.div`
  font-size: 0.6rem;
  color: #94a3b8;
  line-height: 1;
  @media (max-width: 480px) { font-size: 0.58rem; }
`;

const DayStatus = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.15rem;
  font-size: 0.45rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1;
  color: #94a3b8;
  white-space: nowrap;

  @media (max-width: 480px) {
    font-size: 0.52rem;
    padding: 0.06rem 0.3rem;
  }

  @media (max-width: 360px) {
    font-size: 0.5rem;
  }

  .dark & {
    color: #a9b1bc;
    background: #1a1f27;
  }
`;

// Pickup specific time slots (different from store time slots)
const PickupTimeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const PickupTimeSlot = styled.button<{ selected?: boolean; disabled?: boolean }>`
  width: 100%;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  text-align: center;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out, color 0.2s ease-in-out, box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out;
  border: 1px solid ${({ selected, disabled }) =>
    selected ? '#6b7280' : disabled ? '#d1d5db' : '#e5e7eb'};
  background: ${({ selected, disabled }) =>
    selected ? '#374151' : disabled ? '#f3f4f6' : '#ffffff'};
  color: ${({ selected, disabled }) => 
    selected ? '#ffffff' : disabled ? '#6b7280' : '#374151'};
  font-size: 1rem;
  font-weight: 500;
  box-shadow: ${({ selected }) => selected ? '0 4px 12px rgba(0, 0, 0, 0.2)' : 'none'};
  
  &:hover:not(:disabled) {
    border-color: #6b7280;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:disabled {
    pointer-events: none;
  }
  
  .dark & {
    border-color: ${({ selected }) => (selected ? '#3a4250' : '#2e3441')};
    background: ${({ selected, disabled }) => 
      selected ? '#2a2f3a' : disabled ? '#1f2937' : '#0e1116'};
    color: ${({ selected, disabled }) => 
      selected ? '#ffffff' : disabled ? '#9ca3af' : '#e5e7eb'};
  }
`;

const PickupBookedLabel = styled.span`
  display: block;
  margin-top: 0.35rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

// Pickup address components
const PickupAddressGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const PickupAddressRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const PickupAddressColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const PickupAddressLabel = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: #9ca3af;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PickupAddressInput = styled.input`
  padding: 1rem 1.25rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 1rem;
  background: #ffffff;
  color: #1d1d1f;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: #6b7280;
    box-shadow: 0 0 0 3px rgba(107,114,128,0.15);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

// Form card (right side)
const FormCard = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
  height: fit-content;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  .dark & {
    background: #111315;
    border-color: #2e3441;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.6);
  }

  @media (max-width: 1024px) {
    position: static;
    top: auto;
    width: 100%;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    padding: 1.75rem 1.5rem;
    border-radius: 20px;
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
  }

  @media (max-width: 360px) {
    padding: 1.5rem 1.25rem;
    border-radius: 18px;
  }
`;

// Summary section within form card
const SummaryCard = styled.div`
  background: #f8f9fa;
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 1px solid #e5e7eb;
  
  .dark & {
    background: #0b1220;
    border-color: #1f2937;
  }
`;

const SummaryHeader = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  .dark & { color: #e5e7eb; }
`;

const DeviceSlider = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
`;

const CSGroupCard = styled.div`
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 10px 28px rgba(16, 24, 40, 0.06);
  border: 1px solid #e6edf5;
  margin-bottom: 2rem;
  padding: 2rem;
  position: relative;
  overflow: hidden;

  .dark & {
    background: #0f1216;
    border: 1px solid #2e3441;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.6);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 20px;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 14px;
    margin-bottom: 1rem;
  }
`;

const CSRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;

  @media (max-width: 480px) {
    gap: 0.75rem;
  }
`;

const CSImageWrapper = styled.div`
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  border-radius: 12px;
  flex-shrink: 0;

  img {
    max-width: 56px;
    max-height: 56px;
    object-fit: contain;
  }

  .dark & {
    background: #1e293b;
  }

  @media (max-width: 480px) {
    width: 52px;
    height: 52px;
    border-radius: 10px;
    img { max-width: 40px; max-height: 40px; }
  }
`;

const CSTitleCol = styled.div`
  flex: 1;
`;

const CSDetailsToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 100%;
  padding: 0.85rem 0;
  margin-top: 1.25rem;
  background: none;
  border: none;
  border-top: 1px solid #e9edf3;
  border-bottom: 1px solid #e9edf3;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  color: #64748b;
  transition: color 0.15s ease;

  &:hover { color: #334155; }

  .dark & {
    border-color: #1e293b;
    color: #94a3b8;
    &:hover { color: #e2e8f0; }
  }

  @media (max-width: 480px) {
    margin-top: 1rem;
    padding: 0.75rem 0;
    font-size: 0.8rem;
  }
`;

const CSSpecsWrapper = styled.div`
  padding: 1rem 0 0;

  @media (max-width: 480px) {
    padding: 0.75rem 0 0;
  }
`;

const CSSpecsTitle = styled.span`
  font-size: 0.7rem;
  display: block;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 0.25rem;
  padding: 0 0.25rem;

  .dark & { color: #64748b; }

  @media (max-width: 480px) {
    font-size: 0.65rem;
  }
`;

const CSSpecItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.375rem 0.25rem;

  .dark & {
    border-bottom-color: #334155;
  }
`;

const CSSpecLabel = styled.span`
  font-size: 0.9rem;
  color: #1e293b;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;

  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    color: #475569;
  }

  .dark & { color: #e5e7eb; }
  .dark & svg { color: #94a3b8; }

  @media (max-width: 480px) {
    font-size: 0.85rem;
    gap: 8px;
    svg { width: 16px; height: 16px; }
  }
`;

const CSSpecValue = styled.span`
  font-size: 0.825rem;
  font-weight: 600;
  color: #334155;
  flex-shrink: 0;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.3rem 0.7rem;

  .dark & {
    color: #cbd5e1;
    background: #1e293b;
    border-color: #334155;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const CSTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  margin-top: 0.5rem;

  .dark & {
    background: linear-gradient(135deg, #1a1f2e 0%, #151922 100%);
    border-color: #2e3441;
  }

  @media (max-width: 480px) {
    padding: 0.875rem 1rem;
    border-radius: 10px;
  }
`;

const CSTotalLabel = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  .dark & { color: #94a3b8; }
`;

const CSTotalAmount = styled.span`
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f172a;
  display: block;
  letter-spacing: -0.02em;
  line-height: 1.1;

  .dark & { color: #f1f5f9; }

  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const CSTotalVat = styled.span`
  font-size: 0.65rem;
  color: #64748b;
  font-weight: 500;
  display: block;
  margin-top: 0.15rem;

  .dark & { color: #94a3b8; }
`;

const PriceBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #111827;
  background: linear-gradient(180deg, #f3f4f6 0%, #eceff3 100%);
  border: 1px solid #e5e7eb;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.7), 0 1px 2px rgba(16,24,40,0.06);
  
  .dark & {
    color: #e5e7eb;
    background: linear-gradient(180deg, rgba(148,163,184,0.18) 0%, rgba(148,163,184,0.12) 100%);
    border-color: rgba(148,163,184,0.25);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 1px 2px rgba(0,0,0,0.35);
  }
  
  @media (max-width: 480px) {
    font-size: 0.82rem;
    padding: 4px 10px;
  }
  @media (max-width: 360px) {
    font-size: 0.78rem;
    padding: 3px 8px;
  }
`;

const DeviceCategory = styled.div`
  display: inline-block;
  background: rgba(148,163,184,0.12);
  color: #4b5563;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  .dark & { color: #e5e7eb; }
`;

const DeviceCardContainer = styled.div`
  margin-bottom: 1.25rem;
  border-radius: 20px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  border: 1px solid #e6edf5;
  box-shadow: 0 10px 28px rgba(16, 24, 40, 0.06);
  overflow: hidden;

  .dark & {
    background: #0f1116;
    border-color: #1f2937;
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.5);
  }

  @media (max-width: 480px) {
    border-radius: 14px;
    margin-bottom: 1rem;
  }
`;

// Group container to present multiple devices as one cohesive card
const DevicesGroupContainer = styled.div`
  margin-bottom: 0.5rem;
`;

const DeviceFooterRow = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  align-items: center;
  padding: 0.75rem 0 0;
  margin-top: 0.5rem;

  @media (max-width: 480px) {
    grid-template-columns: 32px 1fr 32px;
    padding: 0.5rem 0 0;
    margin-top: 0.25rem;
  }
`;

const NavIconButton = styled.button`
  border: 1px solid #e2e8f0;
  background: #ffffff;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: #cbd5e1;
    color: #334155;
    background: #f8fafc;
  }
  &:active { transform: scale(0.95); }
  &:disabled { opacity: 0.35; cursor: not-allowed; }

  .dark & {
    border-color: #334155;
    background: #1e293b;
    color: #94a3b8;
  }
  .dark &:hover {
    background: #334155;
    color: #e2e8f0;
  }

  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    border-radius: 8px;
  }
`;

const ProgressDots = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const Dot = styled.span<{$active: boolean}>`
  width: ${props => (props.$active ? '20px' : '6px')};
  height: 6px;
  border-radius: 999px;
  background: ${props => (props.$active ? '#334155' : '#d1d5db')};
  transition: width 0.25s ease, background-color 0.25s ease;

  .dark & {
    background: ${props => (props.$active ? '#94a3b8' : '#374151')};
  }
`;

const ShowDetailsButton = styled.button<{ 'data-expanded': boolean }>`
  width: 100%;
  background: #ffffff;
  border: 0;
  border-top: 1px solid #eef2f7;
  color: #111827;
  font-size: 1rem;
  font-weight: 700;
  padding: 1rem 1.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: background-color 0.2s ease, color 0.2s ease;
  
  @media (max-width: 480px) {
    padding: 0.8rem 1rem;
    font-size: 0.95rem;
  }
  @media (max-width: 360px) {
    padding: 0.7rem 0.9rem;
    font-size: 0.9rem;
  }

  &:hover {
    background: #f7f8fa;
    color: #0f172a;
  }

  svg {
    transition: transform 0.2s ease;
    transform: ${({ 'data-expanded': expanded }) => expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
  
  .dark & {
    background: #0f1116;
    border-top-color: #2e3441;
    color: #e5e7eb;
  }
  .dark &:hover {
    background: #13161b;
  }
`;

const DeviceDetailsSection = styled.div<{ show: boolean }>`
  padding: 1.25rem 1.5rem 1.5rem;
  background: #ffffff;
  border: 1px solid #e6edf5; /* duidelijke border in light mode */
  border-radius: 14px;
  animation: ${({ show }) => (show ? 'slideDown' : 'slideUp')} 0.3s ease-out forwards;
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideUp {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(-10px);
    }
  }
  
  .dark & {
    background: #0f1116;
    border-color: #2e3441; /* duidelijke border in dark mode */
  }
  
  @media (max-width: 480px) {
    padding: 1rem 1rem 1.1rem;
    border-radius: 12px;
  }
  @media (max-width: 360px) {
    padding: 0.85rem 0.9rem 1rem;
    border-radius: 10px;
  }
`;

const DeviceDetailsTitle = styled.h4`
  font-size: 1.15rem;
  font-weight: 700;
  color: #111827; /* better contrast in light mode */
  margin: 0 0 1rem 0;
  letter-spacing: -0.01em;
  
  .dark & { color: #e5e7eb; }
  
  @media (max-width: 480px) {
    font-size: 1.05rem;
    margin-bottom: 0.8rem;
  }
  @media (max-width: 360px) {
    font-size: 1rem;
  }
`;

const DeviceDetailsList = styled.div`
  display: flex;
  flex-direction: column;
`;

const DeviceDetailItem = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  padding: 0.85rem 0;
  border-bottom: 1px solid #eef2f7;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  
  .dark & { border-bottom-color: #2e3441; }
  
  @media (max-width: 480px) {
    padding: 0.7rem 0;
  }
`;

const DeviceDetailLabel = styled.div`
  font-size: 0.95rem;
  color: #6e6e73;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  .dark & { color: #94a3b8; }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    gap: 0.6rem;
  }
`;

const DeviceDetailValue = styled.span`
  font-size: 0.95rem;
  color: #1d1d1f;
  font-weight: 600;
  
  .dark & { color: #e5e7eb; }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const SpecIconContainer = styled.div`
  display: flex;
  align-items: center;
  color: #4b5563;
`;

const SpecsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  padding-top: 0.5rem;
`;

const SpecItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #f8fafc;
  padding: 0.75rem;
  border-radius: 12px;
  
  .dark & {
    background: #111827;
  }
`;

const SpecIcon = styled.div`
  color: #4b5563;
  background: rgba(75, 85, 99, 0.12);
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  .dark & {
    color: #e5e7eb;
    background: rgba(107,114,128,0.2);
  }
`;

const SpecTextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SpecLabel = styled.span`
  color: #6e6e73;
  font-size: 0.85rem;
  margin-bottom: 0.1rem;
  
  .dark & { color: #94a3b8; }
`;

const SpecValue = styled.span`
  color: #1d1d1f;
  font-weight: 600;
  
  .dark & { color: #e5e7eb; }
`;

const SubmitButton = styled.button`
  /* This style is kept for other potential uses, but SubmitButtonLarge is primary on this page */
  width: 100%;
  background: #374151;
  color: white;
  border: none;
  border-radius: 14px;
  padding: 1.25rem;
  font-size: 1.05rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out, color 0.2s ease-in-out, box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  margin-top: 1.5rem;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background: #4b5563;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    background: #e5e7eb;
    color: #9ca3af;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;

const SubmitButtonLarge = styled.button`
  width: 100%;
  background: #111827;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  margin-bottom: 0;

  &:hover:not(:disabled) {
    background: #1f2937;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: #e5e7eb;
    color: #9ca3af;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }

  .dark & {
    background: #e5e7eb;
    color: #111827;

    &:hover:not(:disabled) {
      background: #f1f5f9;
    }

    &:disabled {
      background: #334155;
      color: #64748b;
    }
  }
`;

// Helper functions
const createIcon = (IconComponent: any, props?: any) => (
  <IconComponent size={18} {...props} />
);

const getServiceOptions = (t: any) => [
  { 
    key: 'store', 
    title: t('visitStore'),
    subtitle: t('visitStoreSubtitle'),
    icon: FaStore,
    price: t('storeBonus'),
    priceMobile: t('storeBonus'),
    type: 'free' as const
  },
  { 
    key: 'pickup', 
    title: t('pickupService'), 
    subtitle: t('pickupServiceSubtitle'), 
    icon: MdLocalShipping, 
    price: t('free'),
    priceMobile: t('free'),
    type: 'paid' as const 
  },
  // Coming soon: Postal shipping option (disabled)
  {
    key: 'post',
    title: t('shipByPost'),
    subtitle: t('shipByPostSubtitle'),
    icon: FaShippingFast,
    price: t('comingSoon'),
    priceMobile: t('comingSoon'),
    type: 'paid' as const,
    comingSoon: true
  }
];

// Store locations data
const storeLocations = [
  {
    id: 'gent-1',
    name: 'Phone Bros Gent',
    address: 'Lange Violettestraat 6\n9000 Gent',
    phone: '+32 484 89 90 32',
    image: '/images/PSX_20250410_170427.jpg'
  }
];

// Location selection components
const LocationSelector = styled.div`
  display: grid;
  gap: 1rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const LocationCard = styled.button<{ selected?: boolean }>`
  width: 100%;
  max-width: 100%;
  text-align: left;
  background: #ffffff;
  border: 1px solid ${({ selected }) => selected ? '#6b7280' : '#e5e7eb'};
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  box-shadow: ${({ selected }) => selected ?
    '0 0 0 1px #6b7280, 0 8px 16px rgba(0, 0, 0, 0.1)' :
    '0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)'};
  position: relative;
  padding: 0;
  box-sizing: border-box;
  
  @media (max-width: 480px) {
    border-radius: 14px;
  }
  
  @media (max-width: 360px) {
    border-radius: 12px;
  }
  
  &:hover {
    border-color: #6b7280;
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  }
  
  .dark & {
    background: #0e1116;
    border-color: ${({ selected }) => (selected ? '#3a4250' : '#2e3441')};
    box-shadow: ${({ selected }) => selected ? '0 0 0 1px #3a4250, 0 10px 24px rgba(0,0,0,0.55)' : '0 6px 16px rgba(0,0,0,0.5)'};
  }
`;

const LocationImage = styled.div<{ $image?: string }>`
  width: 100%;
  height: 120px;
  background: ${({ $image }) => $image ? `url(${$image}) center 22% / cover no-repeat` : 'linear-gradient(135deg, #f5f5f5 0%, #e9e9e9 100%)'};
  position: relative;
  
  @media (max-width: 480px) {
    height: 100px;
  }
  
  @media (max-width: 360px) {
    height: 85px;
  }
  
  .dark & {
    background: ${({ $image }) => $image ? `url(${$image}) center 22% / cover no-repeat` : 'linear-gradient(135deg, #12151c 0%, #0e1116 100%)'};
  }
`;

const LocationSelectionIndicator = styled.div<{ selected: boolean }>`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${({ selected }) => selected ? '#4b5563' : 'rgba(255, 255, 255, 0.8)'};
  border: 2px solid ${({ selected }) => selected ? '#6b7280' : 'rgba(0, 0, 0, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.8rem;
  backdrop-filter: blur(4px);
  
  .dark & {
    background: ${({ selected }) => selected ? '#4b5563' : 'rgba(15, 23, 42, 0.6)'};
    border-color: ${({ selected }) => selected ? '#6b7280' : 'rgba(30, 41, 59, 0.8)'};
  }
`;

const LocationContent = styled.div`
  padding: 1rem 1.25rem;
  
  @media (max-width: 480px) {
    padding: 0.875rem 1rem;
  }
  
  @media (max-width: 360px) {
    padding: 0.75rem 0.875rem;
  }
`;

const LocationName = styled.h4`
  font-size: 1.05rem;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 0.35rem 0;
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin-bottom: 0.3rem;
  }
  
  @media (max-width: 360px) {
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
  }
  
  .dark & { color: #e5e7eb; }
`;

const LocationAddress = styled.p`
  font-size: 0.9rem;
  color: #6e6e73;
  margin: 0 0 0.35rem 0;
  line-height: 1.45;
  
  @media (max-width: 480px) {
    font-size: 0.82rem;
    margin-bottom: 0.3rem;
    line-height: 1.4;
  }
  
  @media (max-width: 360px) {
    font-size: 0.78rem;
    margin-bottom: 0.25rem;
    line-height: 1.35;
  }
  
  .dark & { color: #94a3b8; }
`;

const LocationPhone = styled.p`
  font-size: 0.9rem;
  color: #4b5563;
  margin: 0;
  font-weight: 500;
  
  @media (max-width: 480px) {
    font-size: 0.82rem;
  }
  
  @media (max-width: 360px) {
    font-size: 0.78rem;
  }
  
  .dark & { color: #e5e7eb; }
`;

const AddressFormField = styled.div`
  position: relative;
`;

const AddressFormLabel = styled.label<{ onWhiteBg?: boolean }>`
  position: absolute;
  top: -0.6em;
  left: 1em;
  font-size: 0.7rem;
  font-weight: 600;
  color: #6e6e73;
  padding: 0 0.5rem;
  background: ${({ onWhiteBg }) => onWhiteBg ? '#ffffff' : '#f7faff'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  span {
    color: #ef4444;
  }
  
  .dark & {
    background: ${({ onWhiteBg }) => onWhiteBg ? '#0f172a' : '#0b1220'};
    color: #94a3b8;
  }
`;

const AddressFormInput = styled.input`
  width: 100%;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  font-size: 1rem;
  background: transparent;
  color: #1d1d1f;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: #6b7280;
    box-shadow: 0 0 0 3px rgba(107,114,128,0.15);
  }
  
  .dark & {
    background: #0f1116;
    border-color: #2e3441;
    color: #e5e7eb;
  }
`;

const AddressFormSelect = styled.select`
  width: 100%;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  font-size: 1rem;
  background: transparent;
  color: #1d1d1f;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236e6e73' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;

  &:focus {
    outline: none;
    border-color: #6b7280;
    box-shadow: 0 0 0 3px rgba(107,114,128,0.15);
  }
  
  .dark & {
    background: #0f172a;
    border-color: #334155;
    color: #e5e7eb;
  }
`;

const AddressFormTextArea = styled.textarea`
  width: 100%;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  font-size: 1rem;
  background: transparent;
  color: #1d1d1f;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
  border-color: #6b7280;
  box-shadow: 0 0 0 3px rgba(107,114,128,0.15);
  }
  
  .dark & {
    background: #0f172a;
    border-color: #334155;
    color: #e5e7eb;
  }
`;

const TimeSlot = styled.button<{ selected?: boolean }>`
  padding: 0.75rem 0.5rem;
  border-radius: 10px;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out, color 0.2s ease-in-out, box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out;
  border: 1px solid ${({ selected }) => selected ? '#6b7280' : '#e5e7eb'};
  background: ${({ selected }) => selected ? '#374151' : '#ffffff'};
  color: ${({ selected }) => selected ? '#ffffff' : '#374151'};
  font-size: 0.95rem;
  font-weight: 500;
  box-shadow: ${({ selected }) => selected ? '0 4px 12px rgba(0, 0, 0, 0.2)' : 'none'};
`;

const TimeSlotCard = styled.button<{ selected?: boolean; disabled?: boolean }>`
  flex-shrink: 0;
  box-sizing: border-box;
  width: 74px;
  min-height: 86px;
  padding: 0.45rem 0.4rem 0.35rem;
  border-radius: 12px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  opacity: ${({ disabled }) => disabled ? 0.5 : 1};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  box-sizing: border-box;
  transition: background-color 0.25s ease-in-out, border-color 0.25s ease-in-out, color 0.25s ease-in-out, box-shadow 0.25s ease-in-out;
  border: 1px solid ${({ selected, disabled }) =>
    selected ? '#6b7280' : disabled ? '#d1d5db' : '#e5e7eb'};
  background: ${({ selected, disabled }) => 
    selected ? '#374151' : disabled ? '#f3f4f6' : '#ffffff'};
  color: ${({ selected, disabled }) => 
    selected ? '#ffffff' : disabled ? '#6b7280' : '#1d1d1f'};
  font-weight: 700;
  font-size: 1.05rem;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  
  @media (max-width: 480px) {
    width: 58px;
    min-height: 72px;
    padding: 0.4rem 0.35rem 0.3rem;
    border-radius: 10px;
    font-size: 0.95rem;
  }
  
  @media (max-width: 360px) {
    width: 54px;
    min-height: 68px;
    padding: 0.35rem 0.3rem 0.28rem;
    border-radius: 10px;
    font-size: 0.9rem;
  }
  
  &:hover:not(:disabled) {
    transform: translateY(-3px);
    border-color: #6b7280;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    cursor: not-allowed;
    pointer-events: none;
  }
  
  .dark & {
    border-color: ${({ selected, disabled }) => 
      selected ? '#3a4250' : disabled ? '#374151' : '#2e3441'};
    background: ${({ selected, disabled }) => 
      selected ? '#2a2f3a' : disabled ? '#1f2937' : '#0e1116'};
    color: ${({ selected, disabled }) => 
      selected ? '#ffffff' : disabled ? '#9ca3af' : '#e5e7eb'};
  }
`;

const BookedText = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  color: #6b7280;
  
  @media (max-width: 480px) {
    font-size: 0.6rem;
  }
  
  @media (max-width: 360px) {
    font-size: 0.55rem;
  }
  
  .dark & {
    color: #9ca3af;
  }
`;

const SkeletonSlot = styled.div`
  flex-shrink: 0;
  width: 74px;
  height: 86px;
  border-radius: 12px;
  background: linear-gradient(
    90deg,
    #f3f4f6 0%,
    #e5e7eb 50%,
    #f3f4f6 100%
  );
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s ease-in-out infinite;
  
  @media (max-width: 480px) {
    width: 58px;
    height: 72px;
    border-radius: 10px;
  }
  
  @media (max-width: 360px) {
    width: 54px;
    height: 68px;
    border-radius: 10px;
  }
  
  .dark & {
    background: linear-gradient(
      90deg,
      #1f2937 0%,
      #374151 50%,
      #1f2937 100%
    );
    background-size: 200% 100%;
  }
  
  @keyframes skeleton-loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
  
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const InfoBox = styled.div`
  background: linear-gradient(135deg, #f0f4ff 0%, #f8fafc 100%);
  border: 1px solid #dbe4f0;
  border-radius: 14px;
  padding: 1.25rem;
  margin: 1rem 0;
  display: flex;
  align-items: flex-start;
  gap: 1rem;

  @media (max-width: 480px) {
    padding: 0.875rem;
    border-radius: 10px;
    gap: 0.625rem;
    margin: 0.75rem 0;
  }

  .dark & {
    background: linear-gradient(135deg, #111827 0%, #0f1216 100%);
    border-color: #1e293b;
  }
`;

const InfoIcon = styled.div`
  background: #0f172a;
  border-radius: 12px;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    svg { width: 18px; height: 18px; }
  }

  .dark & {
    background: #334155;
    color: #e2e8f0;
  }
`;

const InfoText = styled.div`
  color: #334155;
  font-size: 0.85rem;
  line-height: 1.5;
  flex: 1;

  @media (max-width: 480px) {
    font-size: 0.75rem;
    line-height: 1.4;
  }

  h3 {
    margin: 0 0 0.25rem 0;
    font-size: 0.95rem;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.01em;
  }

  @media (max-width: 480px) {
    h3 { font-size: 0.85rem; margin-bottom: 0.15rem; }
  }

  p {
    margin: 0;
    color: #475569;
  }

  .price {
    font-weight: 600;
    color: #0f172a;
  }

  .dark & { color: #cbd5e1; }
  .dark & h3 { color: #f1f5f9; }
  .dark & p { color: #94a3b8; }
  .dark & .price { color: #f1f5f9; }
`;


// Form input components
const FormInputField = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
`;

const FormInputLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #6e6e73;
  margin-bottom: 0.5rem;
  display: block;
  
  span {
    color: #ff3b30;
    margin-left: 2px;
  }
`;

const FormInputBox = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #d2d2d7;
  border-radius: 8px;
  font-size: 16px;
  background: #ffffff;
  color: #1d1d1f;
  transition: border-color 0.2s ease;
  -webkit-appearance: none;

  &:focus {
    outline: none;
    border-color: #6b7280;
  }
  
  &::placeholder {
    color: #aeaeb2;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #1d1d1f;
  cursor: pointer;
  font-weight: 500;
`;

const CheckboxInput = styled.input`
  width: 16px;
  height: 16px;
  accent-color: #6b7280;
  cursor: pointer;
`;

const AppointmentInfoBox = styled.div`
  position: relative;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  padding: 18px 20px 16px;
  border-radius: 16px;
  margin-bottom: 1.5rem;
  border: 1px solid #e6edf5;
  box-shadow: 0 8px 18px rgba(16, 24, 40, 0.06);
  text-align: center;
  
  @media (max-width: 480px) {
    padding: 14px 14px 12px;
    border-radius: 12px;
  }
  
  @media (max-width: 360px) {
    padding: 12px 12px 10px;
    border-radius: 10px;
  }
  
  .dark & {
    background: linear-gradient(180deg, #0f1116 0%, #0b0d13 100%);
    border-color: #2e3441;
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.35);
  }
`;

const AppointmentInfoTitle = styled.div`
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 6px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  
  .dark & {
    color: #94a3b8;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
    margin-bottom: 4px;
  }
  
  @media (max-width: 360px) {
    font-size: 0.7rem;
    margin-bottom: 3px;
  }
`;

const AppointmentInfoDetails = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: #6b7280;
  line-height: 1.5;
  letter-spacing: -0.01em;
  
  .dark & {
    color: #e5e7eb;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    line-height: 1.35;
  }
  
  @media (max-width: 360px) {
    font-size: 0.95rem;
    line-height: 1.3;
  }
`;

const RightSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 1024px) {
    order: 3;
    width: 100%;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    gap: 0.875rem;
    width: 100%;
    max-width: 100%;
  }
`;

const DeviceSummarySection = styled.div`
  @media (min-width: 1025px) {
    grid-column: 1;
    order: 1;
  }
  @media (max-width: 1024px) {
    order: 1;
  }
`;

const StickyContainer = styled.div`
  position: sticky;
  top: 5.5rem;
  @media (max-width: 1024px) {
    position: static;
    top: auto;
  }
`;

const TermsWrapper = styled.div`
  margin-top: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (max-width: 480px) {
    margin-top: 1rem;
    gap: 0.375rem;
  }
`;

const TermsRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: pointer;
  padding: 0.75rem 1rem;
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  transition: all 0.15s ease;

  &:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
  }

  &[data-checked="true"] {
    border-color: #374151;
    background: #f8fafc;
  }

  .dark & {
    background: #0f172a;
    border-color: #334155;
  }
  .dark &:hover {
    border-color: #475569;
    background: rgba(30, 41, 59, 0.5);
  }
  .dark &[data-checked="true"] {
    border-color: #94a3b8;
    background: rgba(30, 41, 59, 0.5);
  }

  @media (max-width: 480px) {
    padding: 0.625rem 0.875rem;
    gap: 0.625rem;
    border-radius: 8px;
  }
`;

const TermsCheckbox = styled.input.attrs({ type: 'checkbox' })`
  && {
    width: 18px;
    height: 18px;
    min-width: 18px;
    border-radius: 5px;
    cursor: pointer;
    accent-color: #374151;
    flex-shrink: 0;
    margin-top: 1px;
  }

  @media (max-width: 480px) {
    && {
      width: 16px;
      height: 16px;
      min-width: 16px;
      border-radius: 4px;
    }
  }
`;

const TermsText = styled.span`
  && {
    font-size: 0.8125rem;
    color: #475569;
    font-weight: 500;
    line-height: 1.4;
  }
  .dark & { color: #94a3b8; }

  @media (max-width: 480px) {
    && { font-size: 0.75rem; }
  }
`;

const TermsLink = styled.button`
  && {
    display: inline;
    padding: 0;
    margin: 0;
    background: none;
    border: none;
    font-family: inherit;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #0f172a;
    cursor: pointer;
    text-decoration: underline;
    text-decoration-color: #94a3b8;
    text-underline-offset: 2px;
    transition: all 0.15s ease;
    line-height: 1.4;
  }

  @media (max-width: 480px) {
    && { font-size: 0.75rem; }
  }
  &&:hover {
    color: #0f172a;
    text-decoration-color: #0f172a;
  }
  .dark & { color: #e2e8f0; text-decoration-color: #475569; }
  .dark &:hover { color: #f8fafc; text-decoration-color: #f8fafc; }
`;

// Notes counter UI
const NotesCounterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 0.25rem 0;
`;

const NotesHint = styled.span`
  font-size: 0.7rem;
  color: #9ca3af;
  margin-top: 0.35rem;

  .dark & { color: #4b5563; }
`;

const NotesCounter = styled.span<{ $warning?: boolean; $over?: boolean }>`
  font-size: 0.75rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${({ $over, $warning }) => ($over ? '#dc2626' : $warning ? '#d97706' : '#9ca3af')};

  .dark & {
    color: ${({ $over, $warning }) => ($over ? '#ef4444' : $warning ? '#f59e0b' : '#4b5563')};
  }
`;

// Legal modals with mobile slide-up animation like HowItWorksModal
const LegalModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  z-index: 10000;
  overflow: hidden;
  touch-action: none;
  pointer-events: auto;
  will-change: opacity;

  @media (max-width: 768px) {
    padding: 0;
    align-items: flex-end;
    overflow-y: hidden;
    -webkit-overflow-scrolling: none;
  }
`;

const LegalModalContent = styled(motion.div)`
  background: #ffffff;
  border-radius: 20px;
  max-width: 900px;
  width: 100%;
  max-height: 85vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.3);
  border: 1.5px solid rgba(17, 24, 39, 0.1);
  display: flex;
  flex-direction: column;
  will-change: transform, opacity;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;

  .dark & {
    background: #0f172a;
    border-color: rgba(71, 85, 105, 0.4);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
  }


  @media (max-width: 768px) {
    width: 100%;
    max-height: 85vh;
    border-radius: 24px 24px 0 0;
    border-bottom: none;
  }
`;

const LegalModalHeader = styled.div`
  padding: 1.75rem 2rem;
  border-bottom: 1px solid rgba(17, 24, 39, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  flex-shrink: 0;
  
  .dark & {
    background: #0f172a;
    border-bottom-color: rgba(71, 85, 105, 0.35);
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem 1.5rem;
    position: relative;
    padding-bottom: 1.25rem;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0.5rem;
      left: 50%;
      transform: translateX(-50%);
      width: 40px;
      height: 4px;
      background: #d1d5db;
      border-radius: 2px;
    }
  }
`;

const LegalModalTitle = styled.h2`
  margin: 0;
  color: #0f172a;
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  
  .dark & {
    color: #f1f5f9;
  }
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const LegalCloseButton = styled.button`
  background: #f8fafc;
  border: 1px solid rgba(17, 24, 39, 0.1);
  border-radius: 10px;
  color: #64748b;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.375rem 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  width: 36px;
  height: 36px;

  &:hover {
    background: #f1f5f9;
    border-color: rgba(17, 24, 39, 0.15);
    color: #0f172a;
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  .dark & {
    background: #1e293b;
    border-color: rgba(71, 85, 105, 0.4);
    color: #94a3b8;
    
    &:hover {
      background: #334155;
      border-color: rgba(71, 85, 105, 0.5);
      color: #e2e8f0;
    }
  }
`;

const LegalModalBody = styled.div`
  padding: 0;
  overflow-y: auto;
  max-height: calc(85vh - 90px);
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f8fafc;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
    
    &:hover {
      background: #94a3b8;
    }
  }
  
  .dark & {
    &::-webkit-scrollbar-track {
      background: #1e293b;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #475569;
      
      &:hover {
        background: #64748b;
      }
    }
  }
  
  @media (max-width: 768px) {
    max-height: calc(90vh - 90px);
  }
`;

const ConfirmButton = styled.button`
  width: 100%;
  padding: 1.1rem 1.25rem;
  border-radius: 16px;
  background: #374151;
  color: white;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  border: 1px solid rgba(75,85,99,0.45);
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out, color 0.2s ease-in-out, box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out;
  margin-top: 1.5rem;
  box-shadow: 0 8px 20px rgba(0,0,0,0.25);

  @media (max-width: 480px) {
    padding: 0.9rem 1rem;
    border-radius: 12px;
    font-size: 0.95rem;
    margin-top: 1rem;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    background: #4b5563;
    box-shadow: 0 10px 24px rgba(0,0,0,0.35);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: #e2e8f0;
    color: #94a3b8;
    border-color: #e2e8f0;
    box-shadow: none;
    cursor: not-allowed;
  }
  
  .dark &:disabled {
    background: #1f2937;
    color: #64748b;
    border-color: #1f2937;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SummarySection = styled.div`
  position: sticky;
  top: 5.5rem;
`;

const CustomerServicePage: React.FC<CustomerServicePageProps> = ({ 
  cartItems, 
  total, 
  onBack, 
  onComplete,
  isSubmitting = false,
  submitError = null
}) => {
  const pageTopRef = useRef<HTMLDivElement>(null);
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState(0);
  const [expandedById, setExpandedById] = useState<Record<string, boolean>>({});
  const [selectedService, setSelectedService] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [currentTimeSlotStart, setCurrentTimeSlotStart] = useState<number>(0);
  const [bookedSlots, setBookedSlots] = useState<string[]>([]);
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [reservationError, setReservationError] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState<boolean>(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState<boolean>(false);
  const [showTerms, setShowTerms] = useState<boolean>(false);
  const [showPrivacy, setShowPrivacy] = useState<boolean>(false);
  const [showDeviceDetails, setShowDeviceDetails] = useState<boolean>(false);
  const [isDetailsRendered, setIsDetailsRendered] = useState<boolean>(false);
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  
  // Refs for trackpad swipe detection
  const dateContainerRef = useRef<HTMLDivElement>(null);
  const timeContainerRef = useRef<HTMLDivElement>(null);

  // Form data state
  const [formData, setFormData] = useState({
    personalInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      alternativePhone: ''
    },
    addressInfo: {
      street: '',
      number: '',
      postalCode: '',
      city: '',
      country: 'Belgium'
    },
    notes: '',
    vatNumber: '',
    companyName: '',
    registeredOffice: ''
  });

  // Business type state
  const [customerType, setCustomerType] = useState<'private' | 'business'>('private');

  const { quantities } = useContext(CartContext);
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(language as Language, key);

  // Detect mobile screen size
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 480;
    }
    return false;
  });

  const [isMobileLegal, setIsMobileLegal] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480);
      setIsMobileLegal(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Animation variants for legal modals (like HowItWorksModal)
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const modalVariants = isMobileLegal
    ? {
        hidden: { y: '100%', opacity: 0 },
        visible: { y: 0, opacity: 1, transition: { type: 'tween' as const, duration: 0.25, ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number] } }
      }
    : {
        hidden: { y: 12, opacity: 0, scale: 0.98 },
        visible: { y: 0, opacity: 1, scale: 1, transition: { type: 'tween' as const, duration: 0.2, ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number] } }
      };

  // Lock body scroll when modals are open (wheel event method - matches repair)
  useEffect(() => {
    if (!(showTerms || showPrivacy)) return;
    const onWheel = (e: WheelEvent) => {
      const overlay = (e.target as HTMLElement)?.closest('[data-legal-overlay]');
      if (!overlay) e.preventDefault();
    };
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => window.removeEventListener('wheel', onWheel);
  }, [showTerms, showPrivacy]);

  // Locale map for all supported languages (affects date/month formatting)
  const localeMap: Record<string, string> = {
    nl: 'nl-NL', en: 'en-US', fr: 'fr-FR', de: 'de-DE', es: 'es-ES',
    tr: 'tr-TR', bg: 'bg-BG', ro: 'ro-RO', sk: 'sk-SK'
  };
  const activeLocale = localeMap[language as string] || 'nl-NL';

  // Format date as: weekday dd/MM/yyyy (e.g., dinsdag 09/09/2025)
  const formatDateWithSlashes = (date: Date): string => {
    try {
      const weekday = new Intl.DateTimeFormat(activeLocale, { weekday: 'long' }).format(date);
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yyyy = date.getFullYear();
      return `${weekday} ${dd}/${mm}/${yyyy}`;
    } catch {
      // Fallback
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yyyy = date.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    }
  };

  const serviceOptions = getServiceOptions(t);

  const currentItem = cartItems[currentDeviceIndex];
  const specs: { key: string; label: string; value: any }[] = [];
  const s = currentItem?.specs || {};

  // Always add storage first if available
  if (s.storage) specs.push({ key: 'storage', label: t('specStorage'), value: s.storage });

  // If dynamicSpecs exists, use it for all answered questions (priority source)
  if (s.dynamicSpecs && s.dynamicSpecs.length > 0) {
    for (const dynamicSpec of s.dynamicSpecs) {
      // Resolve label: JSONB multilingual → hardcoded map → stored snapshot
      const fromJsonbLabel = dynamicSpec.question_labels?.[language] || dynamicSpec.question_labels?.nl;
      const fromMapLabel = getSpecLabel(dynamicSpec.questionKey, language);
      const questionLabel = fromJsonbLabel || (fromMapLabel !== dynamicSpec.questionKey ? fromMapLabel : dynamicSpec.question_label);

      // Resolve value: JSONB multilingual → hardcoded map → stored snapshot
      const fromJsonbValue = dynamicSpec.option_labels?.[language] || dynamicSpec.option_labels?.nl;
      const fromMapValue = getTranslatedValue(dynamicSpec.questionKey, dynamicSpec.optionKey, language);
      const optionValue = fromJsonbValue || (fromMapValue !== dynamicSpec.optionKey ? fromMapValue : dynamicSpec.option_label);

      specs.push({
        key: dynamicSpec.questionKey,
        label: questionLabel,
        value: optionValue
      });
    }
  } else {
    // Fallback: legacy hardcoded specs (for backward compatibility)
    if (s.networkType) specs.push({ key: 'networkType', label: t('specNetwork'), value: s.networkType });
    if (s.battery) specs.push({ key: 'battery', label: t('specBattery'), value: s.battery });
    if (s.condition) specs.push({ key: 'condition', label: t('specCondition'), value: s.condition });
    if (s.functionality) specs.push({ key: 'functionality', label: t('specFunctionality'), value: s.functionality });
    if (s.screenCondition) specs.push({ key: 'screenCondition', label: t('specScreen'), value: s.screenCondition });
    if (s.housingCondition) specs.push({ key: 'housingCondition', label: t('specHousing'), value: s.housingCondition });
    if (s.batteryCapacity) specs.push({ key: 'batteryCapacity', label: t('specBatteryHealth'), value: s.batteryCapacity });
    // iMac
    if (s.screenSize) specs.push({ key: 'screenSize', label: t('specScreenSize'), value: s.screenSize });
    if (s.modelYear) specs.push({ key: 'modelYear', label: t('specModelYear'), value: s.modelYear });
    if (s.processor) specs.push({ key: 'processor', label: t('specProcessor'), value: s.processor });
    if (s.ram) specs.push({ key: 'ram', label: t('specRam'), value: s.ram });
    if (s.accessories) specs.push({ key: 'accessories', label: t('specAccessories'), value: s.accessories });
    // Apple Watch
    if (s.watchSize) specs.push({ key: 'watchSize', label: t('specWatchSize'), value: s.watchSize });
    if (s.watchBand) specs.push({ key: 'watchBand', label: t('specWatchBand'), value: s.watchBand });
    if (s.cellularSupport) specs.push({ key: 'cellularSupport', label: t('specCellular'), value: s.cellularSupport });
  }

  // Always add quantity at the end
  if (quantities[currentItem?.id]) specs.push({ key: 'quantity', label: t('specQuantity'), value: quantities[currentItem.id] });

  const formatSpecValue = (key: string, value: any): React.ReactNode => {
    return getTranslatedValue(key, String(value), language);
  };

  const specIconMap: { [key: string]: any } = {
    storage: FiHardDrive,
    networkType: FiWifi,
    battery: FiBatteryCharging,
    batteryCapacity: FiBatteryCharging,
    condition: FiAward,
    functionality: FiTool,
    screenCondition: FiMonitor,
    housingCondition: FiBox,
    screenSize: FiMonitor,
    modelYear: FiCalendar,
    processor: FiCpu,
    ram: FiServer,
    accessories: FiPackage,
    watchSize: FiClock,
    watchBand: FiPackage,
    cellularSupport: FiWifi,
    quantity: FiHash,
    price: FiTag,
  };

  useEffect(() => {
    if (showDeviceDetails) {
      setIsDetailsRendered(true);
    }
  }, [showDeviceDetails]);

  const handleDetailsAnimationEnd = () => {
    if (!showDeviceDetails) {
      setIsDetailsRendered(false);
    }
  };

  // Robust scroll-to-top when this page mounts
  useEffect(() => {
    const scrollToTop = () => {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // second pass in case layout shifts after initial render
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 120);
      } catch {}
    };
    const timer = setTimeout(scrollToTop, 40);
    return () => clearTimeout(timer);
  }, []);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name } = e.target;
    let { value } = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

    // Enforce notes max length
    if (name === 'notes') {
      value = truncateNotesByLimit(value, NOTES_MAX);
    }
    
    if (name.includes('.')) {
      const [section, field] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...(prev[section as keyof typeof prev] as any),
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle service selection
  const handleServiceSelect = (serviceKey: string) => {
    if (selectedService === serviceKey) {
      setSelectedService('');
      setSelectedLocation('');
      setSelectedDay(null);
      setSelectedTime('');
      setCurrentTimeSlotStart(0);
    } else {
      setSelectedService(serviceKey);
      setSelectedDay(null);
      setSelectedTime('');
      setCurrentTimeSlotStart(0);
      // Auto-select location if only one exists
      if (serviceKey === 'store' && storeLocations.length === 1) {
        setSelectedLocation(storeLocations[0].id);
      } else {
        setSelectedLocation('');
      }
    }
  };

  /**
   * Format a Date object to 'YYYY-MM-DD' string using LOCAL time, not UTC.
   * This prevents timezone-related date shifts (e.g., selecting 17th showing as 16th).
   */
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleLocationSelect = (locationId: string) => {
    // Eğer aynı lokasyonu tekrar seçiyorsa, seçimi kaldır
    if (locationId === selectedLocation) {
      setSelectedLocation('');
      return;
    }

    setSelectedLocation(locationId);
    setReservationError(null);

    if (selectedLocation !== locationId) {
      setSelectedDay(null);
      setSelectedTime('');
    }
  };

  // Generate week days
  const generateWeekDays = () => {
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date(today);
    maxDate.setMonth(maxDate.getMonth() + 1);
    maxDate.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      date.setHours(0, 0, 0, 0);
      
      const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayOfWeek = date.getDay();
      const isToday = date.getTime() === today.getTime();
      
      // Monday is closed
      const isClosed = dayOfWeek === 1;
      
      const isSelected = selectedDay ? 
        (date.getTime() === selectedDay.getTime()) : false;

      const beyondMax = date > maxDate;
      
      // Get first 2-3 characters of translated day name for abbreviation
      const fullDayName = t(dayKeys[dayOfWeek]) || ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'][dayOfWeek];
      const dayAbbr = fullDayName.substring(0, 2).toUpperCase();

      const localeMap: Record<string, string> = {
        nl: 'nl-NL', en: 'en-US', fr: 'fr-FR', de: 'de-DE', es: 'es-ES',
        tr: 'tr-TR', bg: 'bg-BG', ro: 'ro-RO', sk: 'sk-SK'
      };
      const activeLocale = localeMap[language as string] || 'nl-NL';
      const monthFormatter = new Intl.DateTimeFormat(activeLocale, { month: 'short' });
      const monthLabel = monthFormatter.format(date).replace('.', '');
      const yearLabel = String(date.getFullYear());
      
      days.push({
        date,
        dayName: dayAbbr,
        dayNumber: date.getDate(),
        monthLabel,
        yearLabel,
        isToday,
        isPast: date < today || beyondMax,
        isSelected,
        statusText: isClosed ? t('closed') : ''
      });
    }
    
    return days;
  };

  const handleDateSelect = (date: Date) => {
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date(today);
    maxDate.setMonth(maxDate.getMonth() + 1);
    maxDate.setHours(0, 0, 0, 0);
    if (normalizedDate < today || normalizedDate > maxDate) {
      return;
    }

    if (selectedDay && normalizedDate.getTime() === selectedDay.getTime()) {
      // Toggle: Deselect the date
      setSelectedDay(null);
    } else {
      // New date selected
      setSelectedDay(normalizedDate);
    }

    // Reset time selection when date changes
    setSelectedTime('');
  };

  // handleTimeSelect is defined later with booked-slot guard

  const canNavigatePrev = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return currentWeekStart > today;
  }, [currentWeekStart]);

  const getTodayAndMax = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date(today);
    maxDate.setMonth(maxDate.getMonth() + 1);
    maxDate.setHours(0, 0, 0, 0);
    return { today, maxDate };
  };

  const canNavigateNext = useCallback(() => {
    const { maxDate } = getTodayAndMax();
    const nextWeekStart = new Date(currentWeekStart);
    nextWeekStart.setDate(nextWeekStart.getDate() + 7);
    return nextWeekStart <= maxDate;
  }, [currentWeekStart]);

  const navigateWeek = useCallback((direction: 'prev' | 'next') => {
    const { today, maxDate } = getTodayAndMax();
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));

    if (direction === 'prev' && newDate < today) {
      return;
    }
    if (direction === 'next' && newDate > maxDate) {
      return;
    }
    setCurrentWeekStart(newDate);
  }, [currentWeekStart]);

  // Trackpad swipe detection for date navigation
  useEffect(() => {
    const container = dateContainerRef.current;
    if (!container) return;
    
    let accumulatedDeltaX = 0;
    let lastScrollLeft = container.scrollLeft;
    
    const handleWheel = (e: WheelEvent) => {
      // Only handle horizontal swipes (trackpad two-finger swipe)
      // Ignore vertical scrolling
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        const currentScrollLeft = container.scrollLeft;
        const isScrolling = Math.abs(currentScrollLeft - lastScrollLeft) > 1;
        
        // If user is actively scrolling within container, let them scroll
        if (isScrolling) {
          lastScrollLeft = currentScrollLeft;
          accumulatedDeltaX = 0;
          return;
        }
        
        // Check if at scroll boundaries
        const isAtStart = container.scrollLeft <= 5;
        const isAtEnd = Math.abs(container.scrollLeft + container.clientWidth - container.scrollWidth) <= 5;
        
        // Only trigger navigation if at scroll boundaries
        if (isAtStart || isAtEnd) {
          accumulatedDeltaX += e.deltaX;
          
          const threshold = 100;
          
          if (Math.abs(accumulatedDeltaX) >= threshold) {
            if (accumulatedDeltaX > 0 && isAtEnd && canNavigateNext()) {
              // Swipe right at end = next week
              navigateWeek('next');
              accumulatedDeltaX = 0;
            } else if (accumulatedDeltaX < 0 && isAtStart && canNavigatePrev()) {
              // Swipe left at start = previous week
              navigateWeek('prev');
              accumulatedDeltaX = 0;
            } else {
              accumulatedDeltaX = 0;
            }
          }
        }
        
        lastScrollLeft = container.scrollLeft;
        
        // Reset accumulated delta after a short delay
        clearTimeout((handleWheel as any).resetTimeout);
        (handleWheel as any).resetTimeout = setTimeout(() => {
          accumulatedDeltaX = 0;
        }, 300);
      }
    };
    
    const handleScroll = () => {
      lastScrollLeft = container.scrollLeft;
      accumulatedDeltaX = 0;
    };
    
    container.addEventListener('wheel', handleWheel, { passive: true });
    container.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('scroll', handleScroll);
      clearTimeout((handleWheel as any).resetTimeout);
    };
  }, [currentWeekStart, canNavigatePrev, canNavigateNext, navigateWeek]);

  // Time slot navigation functions - filter past slots for today, show rest
  const getVisibleTimeSlots = () => {
    return filterPastSlots(timeSlots);
  };

  // Time slot scroll state
  const [canScrollTimeLeft, setCanScrollTimeLeft] = useState(false);
  const [canScrollTimeRight, setCanScrollTimeRight] = useState(true);

  // Check time container scroll position
  const checkTimeScrollButtons = useCallback(() => {
    const container = timeContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollTimeLeft(scrollLeft > 5);
    setCanScrollTimeRight(scrollLeft < scrollWidth - clientWidth - 5);
  }, []);

  // Effect to check scroll buttons on mount and when slots change
  useEffect(() => {
    const timer = setTimeout(checkTimeScrollButtons, 100);
    return () => clearTimeout(timer);
  }, [selectedDay, checkTimeScrollButtons]);

  const scrollTimeLeft = useCallback(() => {
    const container = timeContainerRef.current;
    if (container) {
      container.scrollBy({ left: -200, behavior: 'smooth' });
      setTimeout(checkTimeScrollButtons, 300);
    }
  }, [checkTimeScrollButtons]);

  const scrollTimeRight = useCallback(() => {
    const container = timeContainerRef.current;
    if (container) {
      container.scrollBy({ left: 200, behavior: 'smooth' });
      setTimeout(checkTimeScrollButtons, 300);
    }
  }, [checkTimeScrollButtons]);

  const navigateTimeSlots = useCallback((direction: 'prev' | 'next') => {
    if (timeContainerRef.current) {
      const scrollAmount = 200;
      timeContainerRef.current.scrollBy({
        left: direction === 'prev' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  }, []);

  /**
   * Build a human-readable summary of all cart items for the appointment.
   * Used to enrich confirmation email and Google Calendar event.
   */
  /**
   * Pickup address on one line: "Kerkstraat 12, 9000 Gent".
   * Returns '' when the customer has not filled it in, so callers can fall back
   * instead of storing a string of stray separators.
   */
  const formatPickupAddress = (): string => {
    const a = formData.addressInfo;
    const streetPart = [a.street, a.number].filter(Boolean).join(' ').trim();
    const cityPart = [a.postalCode, a.city].filter(Boolean).join(' ').trim();
    return [streetPart, cityPart].filter(Boolean).join(', ');
  };

  const buildAppointmentSummary = () => {
    // Build device labels and specs summary
    const deviceLabels = cartItems.map(item => {
      const qty = quantities[item.id] || 1;
      const displayName = formatProductName(item);
      return qty > 1 ? `${qty}x ${displayName}` : displayName;
    });
    
    // Build storage summary (first item or combined)
    const storageLabels = cartItems
      .filter(item => item.specs?.storage)
      .map(item => item.specs.storage);
    
    // Calculate total price
    const totalPrice = cartItems.reduce((acc, item) => {
      if (item.price === -1 || item.price === 0) return acc; // Skip "On Request" items
      const qty = quantities[item.id] || 1;
      return acc + (item.price * qty);
    }, 0);
    
    // Build condition/specs summary from first item (most relevant)
    // NOTE: Calendar/email uses Dutch labels (question_nl, option_nl) for consistent backend logs
    const firstItem = cartItems[0];
    const answerParts: string[] = [];
    if (firstItem?.specs) {
      const s = firstItem.specs;

      // Use dynamicSpecs if available (new approach with all dynamic questions)
      if (s.dynamicSpecs && s.dynamicSpecs.length > 0) {
        for (const spec of s.dynamicSpecs) {
          // Use Dutch labels for calendar/backend (always in Dutch)
          const label = spec.question_nl || spec.question_label || spec.questionKey;
          const value = spec.option_nl || spec.option_label || spec.optionKey;

          answerParts.push(`${label}: ${value}`);
        }
      } else {
        // Legacy fallback: use hardcoded Dutch fields
        if (s.condition) answerParts.push(`Conditie: ${s.condition}`);
        if (s.screenCondition) answerParts.push(`Scherm: ${s.screenCondition}`);
        if (s.housingCondition) answerParts.push(`Behuizing: ${s.housingCondition}`);
        if (s.batteryCapacity) answerParts.push(`Batterij: ${s.batteryCapacity}`);
        if (s.functionality) answerParts.push(`Functionaliteit: ${s.functionality}`);
        // iMac specifics
        if (s.processor) answerParts.push(`Processor: ${s.processor}`);
        if (s.ram) answerParts.push(`RAM: ${s.ram}`);
        if (s.screenSize) answerParts.push(`Scherm: ${s.screenSize}`);
      }
    }
    
    // Get service type label
    const serviceTypeLabel = selectedService === 'store'
      ? t('serviceTypeStore')
      : selectedService === 'pickup'
        ? t('pickupService')
        : t('serviceTypeShipping');
    
    // Get location label
    const locationLabel = selectedService === 'store' && selectedLocation
      ? storeLocations.find(l => l.id === selectedLocation)?.name || selectedLocation
      : selectedService === 'pickup'
        ? formatPickupAddress()
        : '';
    
    // Build cart_items array with full details for email template
    const cart_items = cartItems.map(item => {
      const qty = quantities[item.id] || 1;
      const answers: Array<{ question: string; question_nl?: string; answer: string; answer_nl?: string }> = [];
      if (item.specs?.dynamicSpecs) {
        for (const spec of item.specs.dynamicSpecs) {
          answers.push({
            question: spec.question_labels?.[language] || spec.question_labels?.nl || spec.question_label || spec.questionKey,
            question_nl: spec.question_nl || spec.questionKey,
            answer: spec.option_labels?.[language] || spec.option_labels?.nl || spec.option_label || spec.optionKey,
            answer_nl: spec.option_nl || spec.optionKey,
          });
        }
      }
      return {
        modelName: formatProductName(item),
        deviceType: item.deviceType,
        image: item.image,
        price: item.price,
        quantity: qty,
        storage: item.specs?.storage || undefined,
        answers: answers.length > 0 ? answers : undefined,
      };
    });

    return {
      device_label: deviceLabels.join(', '),
      storage_label: storageLabels.length > 0 ? storageLabels.join(', ') : undefined,
      estimated_price_eur: totalPrice > 0 ? totalPrice : undefined,
      service_type_label: serviceTypeLabel,
      location_label: locationLabel || undefined,
      answers_summary: answerParts.length > 0 ? answerParts.join(', ') : undefined,
      language: language as string,
      notes: formData.notes || undefined,  // notes is at formData root level
      cart_items,
    };
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      return;
    }

    // Direct Booking: Just check that time slot is selected
    if (!selectedDay || !selectedTime) {
      setReservationError(t('selectTimeSlotFirst'));
      return;
    }

    setIsConfirming(true);
    setReservationError(null);

    try {
      const customerName = `${formData.personalInfo.firstName} ${formData.personalInfo.lastName}`;

      // Build appointment summary with device details, price, and condition info
      const appointmentSummary = buildAppointmentSummary();

      // Format date for API (YYYY-MM-DD) - use local date to avoid timezone issues
      const dateStr = formatLocalDate(selectedDay);
      // Pickup bookings used to send `undefined` here, so the address the customer
      // filled in never reached the appointments table (all home bookings had an
      // empty location). Store the full address instead.
      const locationName = selectedService === 'store'
        ? storeLocations.find(l => l.id === selectedLocation)?.name
        : formatPickupAddress() || undefined;

      // Direct booking - backend checks slot availability
      const result = await confirmReservation({
        date: dateStr,
        time: selectedTime,
        serviceType: (selectedService === 'store' ? 'store' : 'home') as ServiceType,
        location: locationName,
        customerName: customerName,
        customerEmail: formData.personalInfo.email,
        customerPhone: formData.personalInfo.phone,
        appointmentSummary: appointmentSummary,
      });

      if (!result.success || !result.appointment) {
        const error = new Error(result.error || t('confirmReservationFailed')) as Error & { code?: string };
        error.code = result.errorCode;
        throw error;
      }

      const confirmedAppointment = result.appointment;
      setAppointment(confirmedAppointment);

      const customer: Customer = {
        id: Date.now().toString(),
        personalInfo: formData.personalInfo,
        addressInfo: formData.addressInfo
      };

      const appointmentDetails = selectedService === 'store' ? {
        location: storeLocations.find(l => l.id === selectedLocation)?.name || '',
        date: selectedDay ? `${selectedDay.getDate().toString().padStart(2, '0')}/${(selectedDay.getMonth() + 1).toString().padStart(2, '0')}/${selectedDay.getFullYear()}` : '',
        time: selectedTime,
        order_number: confirmedAppointment.order_number,
      } : selectedService === 'pickup' ? {
        date: selectedDay ? `${selectedDay.getDate().toString().padStart(2, '0')}/${(selectedDay.getMonth() + 1).toString().padStart(2, '0')}/${selectedDay.getFullYear()}` : '',
        time: selectedTime,
        address: `${formData.addressInfo.number} ${formData.addressInfo.street}, ${formData.addressInfo.city} ${formData.addressInfo.postalCode}`,
        order_number: confirmedAppointment.order_number,
      } : {};

      onComplete(customer, selectedService, appointmentDetails);
    } catch (err: any) {
      console.error('Error confirming reservation:', err);
      let msg = err.message || t('confirmReservationFailed');

      if (err.code === 'SLOT_TAKEN') {
        msg = t('slotJustBooked');
        // Reset to slot selection
        setSelectedTime('');
        // Refresh booked slots
        fetchBookedSlots();
      }

      setReservationError(msg);
    } finally {
      setIsConfirming(false);
    }
  };

  // Check if form is valid
  const isFormValid = () => {
  // Simplified validation for testing
  const hasBasicInfo = formData.personalInfo.firstName && formData.personalInfo.email && formData.personalInfo.phone;
  const hasService = selectedService;
  const hasTerms = acceptedTerms && acceptedPrivacy;
  const hasVatNumber = customerType === 'business' 
    ? (formData.vatNumber.trim() !== '' && formData.companyName.trim() !== '' && formData.registeredOffice.trim() !== '')
    : true;
  
  // Form validation debug removed for production
  
  // For now, just check basic requirements
  if (selectedService === 'store') {
  return hasBasicInfo && hasService && hasTerms && hasVatNumber && selectedLocation && selectedDay && selectedTime;
  }
  
  if (selectedService === 'pickup') {
  return hasBasicInfo && hasService && hasTerms && hasVatNumber && selectedDay && selectedTime && formData.addressInfo.number;
  }
  
  return hasBasicInfo && hasService && hasTerms && hasVatNumber;
  };

  const hasOpAanvraagItem = cartItems.some(item => item.price === -1 || item.price === 0);
  const itemsForTotal = cartItems.filter(item => item.price !== -1 && item.price !== 0);
  const calculatedTotal = itemsForTotal.reduce((acc, item) => acc + (item.price * (quantities[item.id] || 1)), 0);

  // Available time slots - store: every 30min, pickup: every hour
  const storeTimeSlots = [
    '10:00', '10:30',
    '11:00', '11:30', '12:00', '12:30',
    '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30',
    '17:00', '17:30', '18:00', '18:30'
  ];
  const pickupWeekdaySlots = [
    '10:00', '11:00', '12:00', '13:00',
    '14:00', '15:00', '16:00', '17:00', '18:00'
  ];
  const pickupWeekendSlots = ['20:00'];

  const isWeekendDay = selectedDay ? (selectedDay.getDay() === 0 || selectedDay.getDay() === 6) : false;
  const pickupTimeSlots = isWeekendDay ? pickupWeekendSlots : pickupWeekdaySlots;
  const timeSlots = selectedService === 'pickup' ? pickupTimeSlots : storeTimeSlots;

  /**
   * Fetch booked time slots for the selected date from the backend.
   * This enables the UI to show fully booked slots as disabled.
   */
  const fetchBookedSlots = useCallback(async () => {
    if (!selectedDay) {
      setBookedSlots([]);
      return;
    }

    // Format date as YYYY-MM-DD using local time (not UTC)
    const dateStr = formatLocalDate(selectedDay);
    
    // Determine service type for the query
    const serviceType = selectedService === 'store' ? 'store' : 
                        selectedService === 'pickup' ? 'home' : undefined;
    
    // Determine location (only for store service)
    const location = selectedService === 'store' ? selectedLocation : undefined;

    try {
      const bookedTimes = await fetchBookedSlotsForDate({
        date: dateStr,
        location: location || undefined,
        serviceType: serviceType,
      });
      
      console.log('[fetchBookedSlots] Date:', dateStr, 'Booked:', bookedTimes);
      setBookedSlots(bookedTimes);
    } catch (err) {
      console.error('[fetchBookedSlots] Error:', err);
      // On error, show all slots as available (backend will still validate)
      setBookedSlots([]);
    }
  }, [selectedDay, selectedLocation, selectedService]);

  // Fetch booked slots when service/day/location changes
  useEffect(() => {
    if (!selectedService) {
      setBookedSlots([]);
      return;
    }
    
    const shouldFetch = () => {
      if (selectedService === 'store') {
        return selectedDay && selectedLocation;
      }
      if (selectedService === 'pickup') {
        return !!selectedDay;
      }
      return false;
    };
    
    if (shouldFetch()) {
      fetchBookedSlots();
    } else {
      setBookedSlots([]);
    }
  }, [selectedDay, selectedLocation, selectedService, fetchBookedSlots]);

  /**
   * Helper to check if a time slot is booked.
   *
   * Handles both single times ("12:00") and time ranges ("12:00-15:00").
   * The backend returns booked slots as start times only (e.g., ["12:00"]).
   * For time range slots like "12:00-15:00", we extract the start time "12:00" to compare.
   *
   * @param slot - The slot to check (can be "12:00" or "12:00-15:00")
   * @returns true if the slot is booked
   */
  const isSlotBooked = useCallback((slot: string): boolean => {
    const startTime = slot.split('-')[0].trim();
    return bookedSlots.includes(startTime);
  }, [bookedSlots]);

  /** Filter out past time slots when the selected day is today */
  const filterPastSlots = useCallback((slots: string[]): string[] => {
    if (!selectedDay) return slots;
    const today = new Date();
    if (selectedDay.toDateString() !== today.toDateString()) return slots;
    const nowMinutes = today.getHours() * 60 + today.getMinutes() + 30; // 30 min buffer
    return slots.filter(slot => {
      const [h, m] = slot.split('-')[0].trim().split(':').map(Number);
      return (h * 60 + m) > nowMinutes;
    });
  }, [selectedDay]);

  /**
   * Handle time slot selection (Direct Booking).
   *
   * NOTE: No temporary reservation is created here.
   * The slot availability is checked at submit time (handleSubmit).
   * If the slot is taken, user gets an error and can select another slot.
   */
  const handleTimeSelect = (time: string) => {
    // Check if slot is already booked (handles both "12:00" and "12:00-15:00" formats)
    if (isSlotBooked(time)) return;

    // Toggle selection if same time is clicked
    if (time === selectedTime) {
      setSelectedTime('');
      return;
    }

    // Select the new time slot
    setSelectedTime(time);
    setReservationError(null);
  };

  const getSpecIcon = (key: string) => {
    const iconMap: { [key: string]: any } = {
      storage: FiHardDrive,
      condition: FiAward,
      battery: FiBatteryCharging,
      quantity: FiHash,
    };
    return createIcon(iconMap[key] || FaLightbulb);
  };

  return (
    <>
      <StyleInjector />
      <GlobalModalStyles />
      <div ref={pageTopRef} style={{ position: 'absolute', top: 0, height: 0 }} aria-hidden="true" />
      
      <Container>
        <HeaderBox>
          <HeaderContent>
            <PageTitle>{t('finalizeBooking')}</PageTitle>
            <PageSubtitle>{t('selectServiceMethod')}</PageSubtitle>
          </HeaderContent>
          <BackButton onClick={onBack}>
            <BackArrowIcon />
            {t('back')}
          </BackButton>
        </HeaderBox>

        <MainLayout>
          {/* Left Column - Device Details first, then Service Selection */}
          <LeftColumn>
            {/* Device summary card - now at the top */}
            <DeviceSummarySection>
              <DevicesGroupContainer>
                {(() => {
                  const item = cartItems[currentDeviceIndex];
                  const isOpen = !!expandedById[item?.id || ''];
                  if (!item) return null;
                  return (
                    <CSGroupCard>
                      <CSRow>
                        <CSImageWrapper>
                          <img src={encodeURI(item.image)} alt={formatProductName(item)} />
                        </CSImageWrapper>
                        <CSTitleCol>
                          <span style={{ fontSize: isMobile ? '1.05rem' : '1.25rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }} className="dark:text-white">
                            {formatProductName(item, isMobile)}
                          </span>
                          <AnimatePresence mode="wait">
                            {!isOpen && (
                              <motion.span
                                key="header-count"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2, ease: 'easeInOut' }}
                                style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', fontWeight: 500, marginTop: '0.2rem', overflow: 'hidden' }}
                              >
                                {(() => {
                                  const specCount = item.specs?.dynamicSpecs?.length || Object.entries(item.specs || {}).filter(([k, v]) => !['price', 'imageSrc', 'image', 'deviceType', 'storage', 'dynamicSpecs'].includes(k) && v != null && v !== '' && typeof v !== 'object').length;
                                  return `${specCount} ${specCount === 1 ? t('specificationSingular') : t('specificationPlural')}`;
                                })()}
                              </motion.span>
                            )}
                          </AnimatePresence>
                        </CSTitleCol>
                        <AnimatePresence mode="wait">
                          {!isOpen && (
                            <motion.div
                              key="header-price"
                              initial={{ opacity: 0, y: 12 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 12 }}
                              transition={{ duration: 0.25, ease: 'easeInOut' }}
                              style={{ textAlign: 'right', flexShrink: 0 }}
                            >
                              <span style={{ fontSize: isMobile ? '1rem' : '1.25rem', fontWeight: 800, color: (item.price === -1 || item.price === 0) ? '#475569' : '#0f172a', whiteSpace: 'nowrap', display: 'block', letterSpacing: '-0.02em', lineHeight: 1.1 }} className="dark:text-white">
                                {(item.price === -1 || item.price === 0) ? t('priceOnRequest') : formatPrice(item.price, t)}
                              </span>
                              {(item.price === -1 || item.price === 0) && selectedService === 'store' && (
                                <span style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600, display: 'block', marginTop: '0.15rem' }}>
                                  + {formatPrice(15, t)} {t('storeCredit')?.toLowerCase()}
                                </span>
                              )}
                              {selectedService === 'store' && (
                                <span style={{ fontSize: '0.6rem', color: '#16a34a', fontWeight: 600, display: 'block', marginTop: '0.1rem' }}>
                                  + {formatPrice(15, t)} {t('storeCredit')?.toLowerCase()}
                                </span>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </CSRow>

                      <CSDetailsToggle onClick={() => setExpandedById(prev => ({ ...prev, [item.id]: !prev[item.id] }))}>
                        <span>{isOpen ? t('hideDetails') : t('showDetails')}</span>
                        <div style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease', display: 'flex' }}>
                          {createIcon(FaChevronDown)}
                        </div>
                      </CSDetailsToggle>

                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            key="specs-section"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            style={{ overflow: 'hidden' }}
                          >
                            <CSSpecsWrapper>
                              <CSSpecsTitle>{t('deviceSpecifications')}</CSSpecsTitle>
                              {/* Dynamic specifications from Sales Questions */}
                              {item.specs?.dynamicSpecs && item.specs.dynamicSpecs.length > 0 ? (
                                item.specs.dynamicSpecs.map((dynamicSpec: any, idx: number) => {
                                  const fromJsonbLabel2 = dynamicSpec.question_labels?.[language] || dynamicSpec.question_labels?.nl;
                                  const fromMapLabel2 = getSpecLabel(dynamicSpec.questionKey, language);
                                  const questionLabel = fromJsonbLabel2 || (fromMapLabel2 !== dynamicSpec.questionKey ? fromMapLabel2 : dynamicSpec.question_label);
                                  const fromJsonbValue2 = dynamicSpec.option_labels?.[language] || dynamicSpec.option_labels?.nl;
                                  const fromMapValue2 = getTranslatedValue(dynamicSpec.questionKey, dynamicSpec.optionKey, language);
                                  const optionValue = fromJsonbValue2 || (fromMapValue2 !== dynamicSpec.optionKey ? fromMapValue2 : dynamicSpec.option_label);

                                  return (
                                    <React.Fragment key={dynamicSpec.questionKey}>
                                      {idx > 0 && <div style={{ borderBottom: '1px dashed #e2e8f0', margin: '0 0.25rem' }} />}
                                      <CSSpecItem>
                                        <CSSpecLabel>
                                          {getSpecIconFromMapper(dynamicSpec.questionKey, 18)}
                                          <span>{questionLabel}</span>
                                        </CSSpecLabel>
                                        <CSSpecValue>{optionValue}</CSSpecValue>
                                      </CSSpecItem>
                                    </React.Fragment>
                                  );
                                })
                              ) : (
                                /* Fallback: legacy Object.entries approach */
                                Object.entries(item.specs || {})
                                  .filter(([key, value]) =>
                                    !['price', 'imageSrc', 'image', 'deviceType', 'storage', 'dynamicSpecs'].includes(key) &&
                                    value !== null && value !== undefined && value !== '' &&
                                    typeof value !== 'object'
                                  )
                                  .map(([key, value], idx) => {
                                    return (
                                      <React.Fragment key={key}>
                                        {idx > 0 && <div style={{ borderBottom: '1px dashed #e2e8f0', margin: '0 0.25rem' }} />}
                                        <CSSpecItem>
                                          <CSSpecLabel>
                                            {getSpecIconFromMapper(key, 18)}
                                            <span>{getSpecLabel(key, language)}</span>
                                          </CSSpecLabel>
                                          <CSSpecValue>{getTranslatedValue(key, String(value), language)}</CSSpecValue>
                                        </CSSpecItem>
                                      </React.Fragment>
                                    );
                                  })
                              )}
                              <AnimatePresence>
                                {selectedService === 'store' && (
                                  <motion.div
                                    key="store-bonus"
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                                    style={{ overflow: 'hidden' }}
                                  >
                                    <div style={{ borderBottom: '1px dashed #e2e8f0', margin: '0.375rem 0.25rem 0' }} />
                                    <div style={{ paddingTop: '0.75rem' }}>
                                    <CSSpecsTitle>{t('bonus')}</CSSpecsTitle>
                                    <CSSpecItem>
                                      <CSSpecLabel>
                                        {createIcon(FaStore)}
                                        <span>{t('storeBonusLabel')}</span>
                                      </CSSpecLabel>
                                      <CSSpecValue>{formatPrice(15, t)}</CSSpecValue>
                                    </CSSpecItem>
                                    </div>
                                  </motion.div>
                                )}
                                {selectedService === 'pickup' && (
                                  <motion.div
                                    key="pickup-fee"
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                                    style={{ overflow: 'hidden' }}
                                  >
                                    <div style={{ borderBottom: '1px dashed #e2e8f0', margin: '0.375rem 0.25rem 0' }} />
                                    <div style={{ paddingTop: '0.75rem' }}>
                                    <CSSpecsTitle>{t('serviceCosts')}</CSSpecsTitle>
                                    <CSSpecItem>
                                      <CSSpecLabel>
                                        {createIcon(MdLocalShipping)}
                                        <span>{t('pickupService')}</span>
                                      </CSSpecLabel>
                                      <CSSpecValue>{t('free')}</CSSpecValue>
                                    </CSSpecItem>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                              <CSTotalRow>
                                <CSTotalLabel>{t('total')}</CSTotalLabel>
                                <div style={{ textAlign: 'right' }}>
                                  <CSTotalAmount style={{ color: (item.price === -1 || item.price === 0) ? '#475569' : '#0f172a' }}>
                                    {(item.price === -1 || item.price === 0) ? t('priceOnRequest') : formatPrice(item.price, t)}
                                  </CSTotalAmount>
                                  {selectedService === 'store' && (
                                    <span style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600, display: 'block', marginTop: '0.125rem' }}>
                                      + {formatPrice(15, t)} {t('storeCredit')?.toLowerCase()}
                                    </span>
                                  )}
                                </div>
                              </CSTotalRow>
                            </CSSpecsWrapper>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {cartItems.length > 1 && (
                      <DeviceFooterRow>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <NavIconButton
                            onClick={() => setCurrentDeviceIndex(i => Math.max(0, i - 1))}
                            disabled={currentDeviceIndex === 0}
                            aria-label={t('previousDevice')}
                          >
                            {createIcon(FaChevronLeft)}
                          </NavIconButton>
                        </div>
                        <ProgressDots>
                          {cartItems.map((_, idx) => (
                            <Dot key={idx} $active={idx === currentDeviceIndex} />
                          ))}
                        </ProgressDots>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <NavIconButton
                            onClick={() => setCurrentDeviceIndex(i => Math.min(cartItems.length - 1, i + 1))}
                            disabled={currentDeviceIndex === cartItems.length - 1}
                            aria-label={t('nextDevice')}
                          >
                            {createIcon(FaChevronRight)}
                          </NavIconButton>
                        </div>
                      </DeviceFooterRow>
                      )}
                    </CSGroupCard>
                  );
                })()}
              </DevicesGroupContainer>
            </DeviceSummarySection>

            {/* Service Selection Section - now below device details */}
            <ContentCard>
              {/* Service Selection Section */}
              <ServiceSection>
                <SectionHeader>{t('selectServiceMethod')}</SectionHeader>
                <ServiceGrid>
                  {serviceOptions.map(option => (
                    <ServiceOption
                      key={option.key}
                      selected={selectedService === option.key}
                      style={option.comingSoon ? { opacity: 0.7, filter: 'grayscale(0.2)' } : undefined}
                    >
                      <ServiceHeader onClick={(e) => {
                        if (option.comingSoon) {
                          const badge = (e.currentTarget as HTMLElement)?.querySelector?.('[data-coming-soon]') as HTMLElement | null;
                          if (badge) {
                            badge.style.animation = 'subtle-shake 0.5s ease';
                            setTimeout(() => { badge.style.animation = ''; }, 550);
                          }
                          return;
                        }
                        handleServiceSelect(option.key);
                      }}>
                        <ServiceIcon>
                          {createIcon(option.icon)}
                        </ServiceIcon>
                        <ServiceContentWrapper>
                          <ServiceContent>
                            <ServiceTitle>{option.title}</ServiceTitle>
                            <ServiceSubtitle>{option.subtitle}</ServiceSubtitle>
                          </ServiceContent>
                          {option.price && (
                            <ServicePrice
                              type={option.type}
                              data-coming-soon
                              data-full-text={option.price}
                              data-mobile-text={option.priceMobile || option.price}
                            >
                              <span className="desktop-text">{option.price}</span>
                              <span className="mobile-text">{option.priceMobile || option.price}</span>
                            </ServicePrice>
                          )}
                        </ServiceContentWrapper>
                        <ServiceIndicator selected={selectedService === option.key}>
                          {selectedService === option.key && createIcon(FaCheck)}
                        </ServiceIndicator>
                      </ServiceHeader>
                      
                      <AnimatePresence initial={false}>
                        {selectedService === option.key && (
                        <motion.div
                          key={`expanded-${option.key}`}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{
                            height: { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] },
                            opacity: { duration: 0.25, delay: 0.05 },
                          }}
                          style={{ overflow: 'hidden' }}
                        >
                        <ExpandedContent onClick={(e) => e.stopPropagation()}>
                          {option.key === 'store' && (
                              <>
                                <StepContainer>
                                  <SectionTitle $active={!selectedLocation}>
                                    <SectionDot $active={!selectedLocation} />
                                    {t('selectLocation')}
                                  </SectionTitle>
                                  
                                  <LocationSelector>
                                    {storeLocations.map(location => (
                                      <LocationCard
                                        key={location.id}
                                        type="button"
                                        selected={selectedLocation === location.id}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleLocationSelect(location.id);
                                        }}
                                      >
                                        <LocationImage $image={location.image}>
                                          <LocationSelectionIndicator selected={selectedLocation === location.id}>
                                            {selectedLocation === location.id && createIcon(FaCheck)}
                                          </LocationSelectionIndicator>
                                        </LocationImage>
                                        <LocationContent>
                                          <LocationName>{location.name}</LocationName>
                                          <LocationAddress>
                                            {location.address.split('\n').map((line, index) => (
                                              <span key={index}>
                                                {line}
                                                {index < location.address.split('\n').length - 1 && <br />}
                                              </span>
                                            ))}
                                          </LocationAddress>
                                          <LocationPhone>{location.phone}</LocationPhone>
                                        </LocationContent>
                                      </LocationCard>
                                    ))}
                                  </LocationSelector>
                                </StepContainer>

                                {selectedLocation && (
                                  <StepContainer>
                                    <SectionHeaderWithNav>
                                      <SectionTitle $active={!selectedDay}>
                                        <SectionDot $active={!selectedDay} />
                                        {t('selectDate')}
                                      </SectionTitle>
                                      <NavigationButtons>
                                        <NavButton 
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            navigateWeek('prev');
                                          }}
                                          disabled={!canNavigatePrev()}
                                          type="button"
                                        >
                                          {createIcon(FaChevronLeft)}
                                        </NavButton>
                                        <NavButton 
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            navigateWeek('next');
                                          }}
                                          type="button"
                                          className="active"
                                        >
                                          {createIcon(FaChevronRight)}
                                        </NavButton>
                                      </NavigationButtons>
                                    </SectionHeaderWithNav>
                                    
                                    <DaysContainer ref={dateContainerRef}>
                                      {generateWeekDays().map((day, index) => (
                                        <DayCard
                                          key={index}
                                          type="button"
                                          selected={day.isSelected}
                                          disabled={day.isPast || !!day.statusText}
                                          $isToday={day.isToday}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (!day.isPast && !day.statusText) {
                                              handleDateSelect(day.date);
                                            }
                                          }}
                                        >
                                          <DayName>{day.dayName}</DayName>
                                          <DayNumber>{day.dayNumber}</DayNumber>
                                          <DayMonth>{day.monthLabel}</DayMonth>
                                          <DayYear>{day.yearLabel}</DayYear>
                                          {day.statusText && <DayStatus>{day.statusText}</DayStatus>}
                                        </DayCard>
                                      ))}
                                    </DaysContainer>
                                  </StepContainer>
                                )}
                                
                                {selectedLocation && selectedDay && (
                                  <StepContainer>
                                    <SectionHeaderWithNav>
                                      <SectionTitle $active={!selectedTime}>
                                        <SectionDot $active={!selectedTime} />
                                        {t('selectTime')}
                                      </SectionTitle>
                                      <NavigationButtons>
                                        <NavButton
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            navigateTimeSlots('prev');
                                          }}
                                          type="button"
                                        >
                                          {createIcon(FaChevronLeft)}
                                        </NavButton>
                                        <NavButton
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            navigateTimeSlots('next');
                                          }}
                                          type="button"
                                          className="active"
                                        >
                                          {createIcon(FaChevronRight)}
                                        </NavButton>
                                      </NavigationButtons>
                                    </SectionHeaderWithNav>

                                    <DaysContainer ref={timeContainerRef}>
                                      {getVisibleTimeSlots().length === 0 ? (
                                        <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                                          {t('noTimeSlotsAvailable')}
                                        </div>
                                      ) : (
                                        getVisibleTimeSlots().map((time, index) => {
                                          const isBooked = isSlotBooked(time);
                                          return (
                                        <TimeSlotCard
                                          key={time}
                                          type="button"
                                          selected={selectedTime === time}
                                          disabled={isBooked}
                                          aria-disabled={isBooked}
                                          tabIndex={isBooked ? -1 : 0}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (isBooked) return;
                                            handleTimeSelect(time);
                                          }}
                                        >
                                          <span>{time}</span>
                                          {isBooked && <BookedText>{t('slotFullShort')}</BookedText>}
                                        </TimeSlotCard>
                                          );
                                        })
                                      )}
                                    </DaysContainer>
                                  </StepContainer>
                                )}
                              </>
                            )}
                            
                            {option.key === 'pickup' && (
                              <>
                                <InfoBox>
                                  <InfoIcon>
                                    {createIcon(FaLightbulb)}
                                  </InfoIcon>
                                  <InfoText>
                                    <h3>{t('pickupService')}</h3>
                                    <p>{t('pickupCostInfo')}</p>
                                  </InfoText>
                                </InfoBox>
                                
                                <StepContainer>
                                  <SectionHeaderWithNav>
                                    <SectionTitle $active={!selectedDay}>
                                      <SectionDot $active={!selectedDay} />
                                      {t('selecteerDatum')}
                                    </SectionTitle>
                                    <NavigationButtons>
                                      <NavButton 
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          navigateWeek('prev');
                                        }}
                                        disabled={!canNavigatePrev()}
                                        type="button"
                                      >
                                        {createIcon(FaChevronLeft)}
                                      </NavButton>
                                      <NavButton 
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          navigateWeek('next');
                                        }}
                                        type="button"
                                      >
                                        {createIcon(FaChevronRight)}
                                      </NavButton>
                                    </NavigationButtons>
                                  </SectionHeaderWithNav>
                                  
                                  <DaysContainer ref={dateContainerRef}>
                                    {generateWeekDays().map((day, index) => (
                                      <DayCard
                                        key={index}
                                        type="button"
                                        selected={day.isSelected}
                                        disabled={day.isPast || !!day.statusText}
                                        $isToday={day.isToday}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          if (!day.isPast && !day.statusText) {
                                            handleDateSelect(day.date);
                                          }
                                        }}
                                      >
                                        <DayName>{day.dayName}</DayName>
                                        <DayNumber>{day.dayNumber}</DayNumber>
                                        <DayMonth>{day.monthLabel}</DayMonth>
                                        <DayYear>{day.yearLabel}</DayYear>
                                        {day.statusText && <DayStatus>{day.statusText}</DayStatus>}
                                      </DayCard>
                                    ))}
                                  </DaysContainer>
                                </StepContainer>
                                
                                {selectedDay && (
                                  <StepContainer>
                                    <SectionHeaderWithNav>
                                      <SectionTitle $active={!selectedTime}>
                                        <SectionDot $active={!selectedTime} />
                                        {t('selecteerTijd')}
                                      </SectionTitle>
                                      <NavigationButtons>
                                        <NavButton
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            navigateTimeSlots('prev');
                                          }}
                                          type="button"
                                        >
                                          {createIcon(FaChevronLeft)}
                                        </NavButton>
                                        <NavButton
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            navigateTimeSlots('next');
                                          }}
                                          type="button"
                                          className="active"
                                        >
                                          {createIcon(FaChevronRight)}
                                        </NavButton>
                                      </NavigationButtons>
                                    </SectionHeaderWithNav>

                                    <DaysContainer ref={timeContainerRef}>
                                      {getVisibleTimeSlots().length === 0 ? (
                                        <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                                          {t('noTimeSlotsAvailable')}
                                        </div>
                                      ) : (
                                        getVisibleTimeSlots().map((time) => {
                                          const isBooked = isSlotBooked(time);
                                          return (
                                            <TimeSlotCard
                                              key={time}
                                              type="button"
                                              selected={selectedTime === time}
                                              disabled={isBooked}
                                              aria-disabled={isBooked}
                                              tabIndex={isBooked ? -1 : 0}
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (isBooked) return;
                                                handleTimeSelect(time);
                                              }}
                                            >
                                              <span>{time}</span>
                                              {isBooked && <BookedText>{t('slotFullShort')}</BookedText>}
                                            </TimeSlotCard>
                                          );
                                        })
                                      )}
                                    </DaysContainer>
                                  </StepContainer>
                                )}
                                
                                {selectedDay && selectedTime && (
                                  <StepContainer>
                                    <SectionTitle $active={true}>
                                      <SectionDot $active={true} />
                                      {t('pickupAddress')}
                                    </SectionTitle>
                                    <PickupAddressGrid>
                                      <PickupAddressRow>
                                        <AddressFormField>
                                          <AddressFormLabel htmlFor="pickup-street">
                                            {t('streetName')} <span>*</span>
                                          </AddressFormLabel>
                                          <AddressFormInput
                                            id="pickup-street"
                                            type="text"
                                            name="addressInfo.street"
                                            value={formData.addressInfo.street}
                                            onChange={handleInputChange}
                                            onClick={(e) => e.stopPropagation()}
                                            required
                                          />
                                        </AddressFormField>
                                        <AddressFormField>
                                          <AddressFormLabel htmlFor="pickup-number">
                                            {t('houseNumber')} <span>*</span>
                                          </AddressFormLabel>
                                          <AddressFormInput
                                            id="pickup-number"
                                            type="text"
                                            name="addressInfo.number"
                                            value={formData.addressInfo.number}
                                            onChange={handleInputChange}
                                            onClick={(e) => e.stopPropagation()}
                                            required
                                          />
                                        </AddressFormField>
                                      </PickupAddressRow>
                                      <PickupAddressRow>
                                        <AddressFormField>
                                          <AddressFormLabel htmlFor="pickup-zip">
                                            {t('postcode')} <span>*</span>
                                          </AddressFormLabel>
                                          <AddressFormInput
                                            id="pickup-zip"
                                            type="text"
                                            name="addressInfo.postalCode"
                                            value={formData.addressInfo.postalCode}
                                            onChange={handleInputChange}
                                            onClick={(e) => e.stopPropagation()}
                                            required
                                          />
                                        </AddressFormField>
                                        <AddressFormField>
                                          <AddressFormLabel htmlFor="pickup-city">
                                            {t('plaats')} <span>*</span>
                                          </AddressFormLabel>
                                          <AddressFormInput
                                            id="pickup-city"
                                            type="text"
                                            name="addressInfo.city"
                                            value={formData.addressInfo.city}
                                            onChange={handleInputChange}
                                            onClick={(e) => e.stopPropagation()}
                                            required
                                          />
                                        </AddressFormField>
                                      </PickupAddressRow>
                                    </PickupAddressGrid>
                                  </StepContainer>
                                )}
                              </>
                            )}
                          </ExpandedContent>
                        </motion.div>
                        )}
                      </AnimatePresence>
                      </ServiceOption>
                    ))}
                  </ServiceGrid>
                </ServiceSection>
              </ContentCard>
            </LeftColumn>

          {/* Right Side - Form */}
          <RightSide>
            <StickyContainer>
              <FormCard>
                {/* Top Section - Service Selection Info */}
                {selectedService && (
                  <AppointmentInfoBox>
                    <AppointmentInfoTitle>
                      {serviceOptions.find(opt => opt.key === selectedService)?.title || t('selectedService')}
                    </AppointmentInfoTitle>
                    {selectedService === 'store' && selectedLocation && selectedDay && selectedTime && (
                      <AppointmentInfoDetails>
                        {t('appointmentAt')} {selectedTime}<br />
                        {t('on')} {formatDateWithSlashes(selectedDay)}
                      </AppointmentInfoDetails>
                    )}
                    {selectedService === 'pickup' && selectedDay && selectedTime && (
                      <AppointmentInfoDetails>
                        {t('pickupAt')} {selectedTime}<br />
                        {t('on')} {formatDateWithSlashes(selectedDay)}
                      </AppointmentInfoDetails>
                    )}
                  </AppointmentInfoBox>
                )}

                {/* Device list moved above; form begins here */}

                <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                  {/* Customer Type Selection */}
                  <CustomerTypeToggle>
                    <ToggleButton
                      selected={customerType === 'private'}
                      onClick={() => setCustomerType('private')}
                    >
                      {t('private')}
                    </ToggleButton>
                    <ToggleButton
                      selected={customerType === 'business'}
                      onClick={() => setCustomerType('business')}
                    >
                      {t('business')}
                    </ToggleButton>
                  </CustomerTypeToggle>
                  
                  <FormGrid>
                    {customerType === 'business' && (
                      <>
                        <FormField style={{ gridColumn: '1 / -1' }}>
                          <FormLabel>{t('vatNumber')} <span>*</span></FormLabel>
                          <FormInput
                            type="text"
                            name="vatNumber"
                            value={formData.vatNumber}
                            onChange={handleInputChange}
                            placeholder="BE0123456789"
                            required
                          />
                        </FormField>
                        <FormField>
                          <FormLabel>{t('companyName')} <span>*</span></FormLabel>
                          <FormInput
                            type="text"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleInputChange}
                            placeholder={t('companyName')}
                            required
                          />
                        </FormField>
                        <FormField>
                          <FormLabel>{t('registeredOffice')} <span>*</span></FormLabel>
                          <FormInput
                            type="text"
                            name="registeredOffice"
                            value={formData.registeredOffice}
                            onChange={handleInputChange}
                            placeholder={t('registeredOffice')}
                            required
                          />
                        </FormField>
                      </>
                    )}
                    
                    <FormField>
                      <FormLabel>{t('firstName')} <span>*</span></FormLabel>
                      <InputWrapper>
                        <InputIcon>{createIcon(FiUser)}</InputIcon>
                        <FormInput
                          data-with-icon="true"
                          type="text"
                          name="personalInfo.firstName"
                          value={formData.personalInfo.firstName}
                          onChange={handleInputChange}
                          placeholder={t('firstName')}
                          required
                        />
                      </InputWrapper>
                    </FormField>
                    <FormField>
                      <FormLabel>{t('lastName')} <span>*</span></FormLabel>
                      <InputWrapper>
                        <InputIcon>{createIcon(FiUser)}</InputIcon>
                        <FormInput
                          data-with-icon="true"
                          type="text"
                          name="personalInfo.lastName"
                          value={formData.personalInfo.lastName}
                          onChange={handleInputChange}
                          placeholder={t('lastName')}
                          required
                        />
                      </InputWrapper>
                    </FormField>
                    
                    <FormField>
                      <FormLabel>{t('phone')} <span>*</span></FormLabel>
                      <InputWrapper>
                        <InputIcon>{createIcon(FiPhone)}</InputIcon>
                        <FormInput
                          data-with-icon="true"
                          type="tel"
                          name="personalInfo.phone"
                          value={formData.personalInfo.phone}
                          onChange={handleInputChange}
                          placeholder="+32470000000"
                          required
                        />
                      </InputWrapper>
                    </FormField>
                    <FormField>
                      <FormLabel>{t('alternativePhone')}</FormLabel>
                      <InputWrapper>
                        <InputIcon>{createIcon(FiPhone)}</InputIcon>
                        <FormInput
                          data-with-icon="true"
                          type="tel"
                          name="personalInfo.alternativePhone"
                          value={formData.personalInfo.alternativePhone || ''}
                          onChange={handleInputChange}
                          placeholder="+32470000000"
                        />
                      </InputWrapper>
                    </FormField>
                    
                    <FormField style={{ gridColumn: '1 / -1' }}>
                      <FormLabel>{t('email')} <span>*</span></FormLabel>
                      <InputWrapper>
                        <InputIcon>{createIcon(FiMail)}</InputIcon>
                        <FormInput
                          data-with-icon="true"
                          type="email"
                          name="personalInfo.email"
                          value={formData.personalInfo.email}
                          onChange={handleInputChange}
                          placeholder="ikverkoopmijntoestel@gmail.com"
                          required
                        />
                      </InputWrapper>
                    </FormField>
                    
                    <FormField style={{ gridColumn: '1 / -1' }}>
                      <NotesCounterRow>
                        <FormLabel style={{ margin: 0 }}>{t('notes')}</FormLabel>
                        <NotesCounter
                          $warning={getEffectiveNotesLength(formData.notes) > NOTES_MAX - 100 && getEffectiveNotesLength(formData.notes) <= NOTES_MAX}
                          $over={getEffectiveNotesLength(formData.notes) > NOTES_MAX}
                        >
                          {Math.max(0, NOTES_MAX - getEffectiveNotesLength(formData.notes))} / {NOTES_MAX}
                        </NotesCounter>
                      </NotesCounterRow>
                      <InputWrapper style={{ width: '100%' }}>
                        <InputIcon style={{ top: '1.25rem', transform: 'translateY(0)' }}>{createIcon(FiEdit2)}</InputIcon>
                        <FormTextArea
                          name="notes"
                          value={formData.notes}
                          onChange={handleInputChange}
                          placeholder={t('notesPlaceholder') || "bv. speciale instructies, vragen, etc."}
                          style={{ paddingLeft: '3rem', width: '100%' }}
                        />
                      </InputWrapper>
                      <NotesHint>{t('messageLimitNote').replace('{0}', String(NOTES_MAX))}</NotesHint>
                    </FormField>
                  </FormGrid>
                  
                  {/* Terms and conditions */}
                  <TermsWrapper>
                    <TermsRow data-checked={acceptedTerms}>
                      <TermsCheckbox
                        id="terms"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                      />
                      <span>
                        <TermsText>{t('acceptTerms')} </TermsText>
                        <TermsLink type="button" onClick={(e) => { e.preventDefault(); setShowTerms(true); }}>{t('termsConditions')}</TermsLink>
                      </span>
                    </TermsRow>

                    <TermsRow data-checked={acceptedPrivacy}>
                      <TermsCheckbox
                        id="privacy"
                        checked={acceptedPrivacy}
                        onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                      />
                      <span>
                        <TermsText>{t('acceptPrivacy')} </TermsText>
                        <TermsLink type="button" onClick={(e) => { e.preventDefault(); setShowPrivacy(true); }}>{t('privacyPolicy')}</TermsLink>
                      </span>
                    </TermsRow>
                  </TermsWrapper>
                </form>
                
                {/* Reservation error message */}
                {reservationError && (
                  <div style={{ 
                    marginTop: '1rem', 
                    padding: '0.875rem 1rem', 
                    backgroundColor: '#fee2e2', 
                    border: '1px solid #fecaca', 
                    borderRadius: '8px', 
                    color: '#991b1b',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <span>⚠️ {reservationError}</span>
                    <button
                      onClick={() => setReservationError(null)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#991b1b',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        padding: '0 0.5rem',
                        marginLeft: '0.5rem'
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}
                
                {/* Error message */}
                {submitError && (
                  <div style={{ 
                    marginTop: '1rem', 
                    padding: '0.875rem 1rem', 
                    backgroundColor: '#fee2e2', 
                    border: '1px solid #fecaca', 
                    borderRadius: '8px', 
                    color: '#991b1b',
                    fontSize: '0.875rem'
                  }}>
                    ⚠️ {submitError}
                  </div>
                )}
                
                {/* Submit button - moved outside form but inside FormCard */}
                <div style={{ padding: '0 0 0', marginTop: '1.5rem' }}>
                  <ConfirmButton type="button" onClick={handleSubmit} disabled={!isFormValid() || isSubmitting || isConfirming}>
                    {isConfirming ? (
                      <>
                        <span style={{ display: 'inline-block', width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite', marginRight: '8px' }} />
                        {t('confirmingReservation')}
                      </>
                    ) : isSubmitting ? (
                      <>
                        <span style={{ display: 'inline-block', width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite', marginRight: '8px' }} />
                        {t('bookingInProgress')}
                      </>
                    ) : (
                      t('confirmBooking')
                    )}
                  </ConfirmButton>
                </div>
              </FormCard>
            </StickyContainer>
          </RightSide>
        </MainLayout>
      </Container>

      {/* Legal modals */}
      {createPortal(
        <>
          <AnimatePresence>
            {showTerms && (
              <LegalModalOverlay
                data-legal-overlay
                key="terms-overlay"
                onClick={() => setShowTerms(false)}
                variants={overlayVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                transition={{ duration: 0.2 }}
              >
                <LegalModalContent
                  key="terms-modal"
                  onClick={(e) => e.stopPropagation()}
                  variants={modalVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                >
                  <LegalModalHeader>
                    <LegalModalTitle>{t('algemeneVoorwaardenTitle')}</LegalModalTitle>
                    <LegalCloseButton onClick={() => setShowTerms(false)}>×</LegalCloseButton>
                  </LegalModalHeader>
                  <LegalModalBody>
                    <TermsContent />
                  </LegalModalBody>
                </LegalModalContent>
              </LegalModalOverlay>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showPrivacy && (
              <LegalModalOverlay
                data-legal-overlay
                key="privacy-overlay"
                onClick={() => setShowPrivacy(false)}
                variants={overlayVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                transition={{ duration: 0.2 }}
              >
                <LegalModalContent
                  key="privacy-modal"
                  onClick={(e) => e.stopPropagation()}
                  variants={modalVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                >
                  <LegalModalHeader>
                    <LegalModalTitle>{t('privacyPolicyTitle')}</LegalModalTitle>
                    <LegalCloseButton onClick={() => setShowPrivacy(false)}>×</LegalCloseButton>
                  </LegalModalHeader>
                  <LegalModalBody>
                    <PrivacyContent />
                  </LegalModalBody>
                </LegalModalContent>
              </LegalModalOverlay>
            )}
          </AnimatePresence>
        </>,
        document.body
      )}
    </>
  );
};

export default CustomerServicePage;